<template>
  <transition name="modal-fade">
    <div v-if="visible" class="account-modal-overlay" @click.self="handleClose">
      <div class="account-modal-container">
        <aside class="account-sidebar">
          <div class="sidebar-logo">
            <img :src="timeClubLogo" alt="TIME Club" />
          </div>
          <nav class="sidebar-nav">
            <button
              v-for="item in navItems"
              :key="item.id"
              class="sidebar-nav-item"
              :class="{ active: activeTab === item.id }"
              :title="$t(item.labelKey)"
              @click="handleTabClick(item.id)"
            >
              <span class="sidebar-nav-icon" v-html="item.icon"></span>
            </button>
          </nav>
          <button class="sidebar-logout" :title="$t('account_modal.logout')" @click="handleLogout">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          </button>
        </aside>

        <main class="account-main">
          <div class="account-main-header">
            <h2 class="account-main-title">{{ activeTabLabel }}</h2>
            <button class="account-close-btn" @click="handleClose" :aria-label="$t('common.close')">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <div v-if="activeTab === 'overview'" class="account-overview">
            <div class="overview-top">
              <div class="member-card">
                <div class="member-card-inner">
                  <div class="member-level">{{ memberLevelText }}</div>
                  <div class="member-name">{{ displayName }}</div>
                  <div class="member-spending">
                    <span class="spending-label">{{ $t('account_modal.member_card.cumulative_spending') }}</span>
                    <span class="spending-value">USD${{ Number(memberSpending).toFixed(2) }}</span>
                  </div>
                  <div class="member-progress">
                    <div class="progress-track">
                      <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
                      <div class="progress-lock">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        <span>{{ discountHint }}</span>
                      </div>
                    </div>
                    <p v-if="memberUpgradeStatus === 'spending'" class="progress-hint">{{ $t('account_modal.member_card.spending_hint_with_level', { amount: spendingToUpgrade, level: nextRankName }) }}</p>
                    <p v-else-if="memberUpgradeStatus === 'ready'" class="progress-hint">{{ $t('account_modal.member_card.upgrade_pending') }}</p>
                    <p v-else class="progress-hint">{{ $t('account_modal.member_card.highest_level') }}</p>
                  </div>
                  <div class="member-rule-links">
                    <a href="javascript:;" class="rule-link" @click="openRuleModal('points')">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                      {{ $t('account_modal.rule.points_rule') }}
                    </a>
                    <a href="javascript:;" class="rule-link" @click="openRuleModal('rank')">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                      {{ $t('account_modal.rule.rank_rule') }}
                    </a>
                  </div>
                </div>
              </div>

              <div class="overview-right">
                <div class="points-card">
                  <div class="points-card-bg"></div>
                  <div class="points-card-content">
                    <div class="points-icon-wrap">
                      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                    </div>
                    <div class="points-label">{{ $t('account_modal.points_card.title') }}</div>
                    <div class="points-amount">
                      <span class="points-num">{{ clubPoints }}</span>
                      <span class="points-unit">{{ $t('redemption_modal.points') }}</span>
                    </div>
                    <p class="points-tip">{{ $t('account_modal.points_card.tip') }}</p>
                    <button class="points-redeem-btn" @click="openRedemptionModal">
                      <span>{{ $t('account_modal.points_card.redeem_btn') }}</span>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="overview-bottom">
              <div class="section-card">
                <div class="section-header">
                  <h3>{{ $t('account_modal.latest_orders') }}</h3>
                  <button class="section-more" @click="activeTab = 'orders'">{{ $t('account_modal.more') }}</button>
                </div>
                <div v-if="userOrders.length === 0" class="section-empty">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <p>{{ $t('account_modal.no_orders') }}</p>
                  <button class="section-action-btn" @click="goShopping">{{ $t('account_modal.shop_now') }}</button>
                </div>
                <div v-else class="section-list">
                  <div
                    v-for="order in userOrders.slice(0, 3)"
                    :key="order.orderNumber || order.id"
                    class="section-list-item"
                    @click="selectedOrder = order"
                  >
                    <div class="item-info">
                      <p class="item-title">{{ order.orderNumber || order.id }}</p>
                      <p class="item-date">{{ formatOrderDate(order.date) }}</p>
                    </div>
                    <div class="item-status">
                      <span class="status-badge" :class="order.status">{{ $t(`account_modal.order_status.${order.status}`) }}</span>
                      <span class="item-amount">USD${{ order.total }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="section-card">
                <div class="section-header">
                  <h3>{{ $t('account_modal.latest_coupons') }}</h3>
                  <button class="section-more" @click="activeTab = 'coupons'">{{ $t('account_modal.more') }}</button>
                </div>
                <div v-if="availableCoupons.length === 0" class="section-empty">
                  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                  <p>{{ $t('account_modal.no_coupons') }}</p>
                  <button class="section-action-btn" @click="goShopping">{{ $t('account_modal.shop_now') }}</button>
                </div>
                <div v-else class="section-list">
                  <div
                    v-for="coupon in availableCoupons.slice(0, 3)"
                    :key="coupon.id"
                    class="section-list-item"
                  >
                    <div class="item-info">
                      <p class="item-title">{{ coupon.title }}</p>
                      <p class="item-date">{{ $t('account_modal.coupons_page.min_spend', { amount: coupon.minSpend }) }}</p>
                    </div>
                    <div class="item-status">
                      <span class="coupon-value">USD${{ coupon.value }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="activeTab === 'profile'" class="account-profile">
            <div class="profile-toolbar">
              <span class="profile-meta"></span>
              <div class="profile-actions">
                <!-- <button type="button" class="profile-btn profile-btn-outline" @click="handleManageAddress">
                  {{ $t('account_modal.profile_toolbar.manage_address') }}
                </button> -->
                <button type="button" class="profile-btn profile-btn-primary" @click="handleSaveProfile">
                  {{ $t('account_modal.profile_toolbar.save') }}
                </button>
              </div>
            </div>

            <form class="profile-form" @submit.prevent="handleSaveProfile">
              <div class="profile-field profile-field-full">
                <label class="profile-label">{{ $t('account_modal.profile_form.email') }}</label>
                <input
                  :value="profileForm.email"
                  type="email"
                  class="profile-input profile-input-readonly"
                  readonly
                />
              </div>

              <div class="profile-field-row profile-field-row-name">
                <div class="profile-field profile-field-salutation">
                  <label class="profile-label">{{ $t('account_modal.profile_form.salutation') }}</label>
                  <select v-model="profileForm.salutation" class="profile-select">
                    <option value="mr">{{ $t('account_modal.profile_form.salutation_mr') }}</option>
                    <option value="mrs">{{ $t('account_modal.profile_form.salutation_mrs') }}</option>
                    <option value="ms">{{ $t('account_modal.profile_form.salutation_ms') }}</option>
                  </select>
                </div>
                <div class="profile-field">
                  <label class="profile-label">{{ $t('account_modal.profile_form.name') }}</label>
                  <input v-model="profileForm.name" type="text" class="profile-input" />
                </div>
              </div>

              <div class="profile-field-row">
                <div class="profile-field profile-field-region">
                  <label class="profile-label">{{ $t('account_modal.profile_form.phone') }}</label>
                  <select v-model="profileForm.phoneRegion" class="profile-select">
                    <option value="+86">{{ $t('account_modal.address_form.phone_regions.cn') }}</option>
                  </select>
                </div>
                <div class="profile-field profile-field-phone">
                  <label class="profile-label profile-label-hidden">{{ $t('account_modal.profile_form.phone_number') }}</label>
                  <input disabled v-model="profileForm.phone" type="tel" class="profile-input profile-input-readonly" />
                </div>
              </div>

              <div class="profile-field profile-field-full">
                <label class="profile-label">{{ $t('account_modal.profile_form.address') }}</label>
                <input
                  v-model="profileForm.address"
                  type="text"
                  class="profile-input"
                  :placeholder="$t('account_modal.profile_form.address_placeholder')"
                />
              </div>
              <!-- 微信号 -->
              <div class="profile-field profile-field-full">
                <label class="profile-label">{{ $t('account_modal.profile_form.weixin') }}</label>
                <input
                  v-model="profileForm.weixin"
                  type="text"
                  class="profile-input"
                  :placeholder="$t('account_modal.profile_form.weixin_placeholder')"
                />
              </div>
              <div class="profile-field-row">
                <div class="profile-field profile-field-password">
                  <label class="profile-label">{{ $t('account_modal.profile_form.password') }}</label>
                  <div class="password-row">
                    <input
                      v-model="profileForm.password"
                      :type="showProfilePassword ? 'text' : 'password'"
                      class="profile-input password-input"
                      :placeholder="$t('account_modal.profile_form.password_placeholder')"
                    />
                    <button
                      type="button"
                      class="btn-toggle-password"
                      @click="showProfilePassword = !showProfilePassword"
                      :aria-label="showProfilePassword ? 'Hide password' : 'Show password'"
                    >
                      <svg
                        v-if="!showProfilePassword"
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div class="profile-field profile-field-password">
                  <label class="profile-label">{{ $t('account_modal.profile_form.confirm_password') }}</label>
                  <div class="password-row">
                    <input
                      v-model="profileForm.confirmPassword"
                      :type="showProfileConfirmPassword ? 'text' : 'password'"
                      class="profile-input password-input"
                      :placeholder="$t('account_modal.profile_form.password_placeholder')"
                    />
                    <button
                      type="button"
                      class="btn-toggle-password"
                      @click="showProfileConfirmPassword = !showProfileConfirmPassword"
                      :aria-label="showProfileConfirmPassword ? 'Hide password' : 'Show password'"
                    >
                      <svg
                        v-if="!showProfileConfirmPassword"
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div class="profile-field-row profile-field-row-inline">
                <div class="profile-field profile-field-birth">
                  <label class="profile-label">{{ $t('account_modal.profile_form.birth_date') }}</label>
                  <div class="profile-date-group">
                    <select v-model="profileForm.birthYear" class="profile-select">
                      <option v-for="y in birthYears" :key="y" :value="y">{{ y }}</option>
                    </select>
                    <select v-model="profileForm.birthMonth" class="profile-select">
                      <option v-for="m in 12" :key="m" :value="m">{{ m }}</option>
                    </select>
                    <select v-model="profileForm.birthDay" class="profile-select">
                      <option v-for="d in 31" :key="d" :value="d">{{ d }}</option>
                    </select>
                  </div>
                </div>
                <div class="profile-field profile-field-lang">
                  <label class="profile-label">{{ $t('account_modal.profile_form.language') }}</label>
                  <select v-model="profileForm.language" class="profile-select">
                    <option value="">{{ $t('account_modal.profile_form.select_language') }}</option>
                    <option value="繁體中文">{{ $t('account_modal.profile_form.lang_zh_tw') }}</option>
                    <option value="中文简体">{{ $t('account_modal.profile_form.lang_zh_cn') }}</option>
                    <option value="English">{{ $t('account_modal.profile_form.lang_en') }}</option>
                  </select>
                </div>
              </div>

              <label class="profile-consent">
                <input v-model="profileForm.marketingConsent" type="checkbox" />
                <span>{{ $t('account_modal.profile_form.marketing_consent') }}</span>
              </label>
            </form>
          </div>

          <div v-else-if="activeTab === 'orders'" class="account-orders-page">
            <!-- <div class="list-toolbar"> -->
              <!-- <span class="profile-meta">{{ clubPoints }} {{ $t('timeclub.points') }} / {{ $t('account_modal.profile_form.language') }}: {{ memberLevel }}</span> -->
            <!-- </div> -->
            <div class="orders-section">
              <div class="list-section-header">
                <span class="list-section-icon" v-html="activeTabIcon"></span>
                <h3 class="list-section-title">{{ $t('account_modal.orders_page.title') }}</h3>
              </div>
              <div class="list-divider"></div>

              <div v-if="userOrders.length === 0" class="list-empty">
                <p>{{ $t('common.no_data') }}</p>
              </div>

              <div v-else class="orders-table">
                <div class="orders-table-head">
                  <span class="orders-col orders-col-id">{{ $t('account_modal.orders_page.order_number') }}</span>
                  <span class="orders-col orders-col-recipient">{{ $t('account_modal.orders_page.recipient') }}</span>
                  <span class="orders-col orders-col-total">{{ $t('account_modal.orders_page.order_total') }}</span>
                  <span class="orders-col orders-col-actions"></span>
                </div>
                <div
                  v-for="order in userOrders"
                  :key="order.id"
                  class="orders-table-row"
                >
                  <div class="orders-col orders-col-id">
                    <p class="order-number">{{ order.orderNumber || order.id }}</p>
                    <p class="order-date">{{ formatOrderDate(order.date) }}</p>
                  </div>
                  <div class="orders-col orders-col-recipient">
                    <p class="order-recipient">{{ orderRecipient(order) }}</p>
                  </div>
                  <div class="orders-col orders-col-total">
                    <span class="order-total">
                      USD $<span class="order-total-amount">{{ formatOrderAmount(order.total) }}</span>
                    </span>
                  </div>
                  <div class="orders-col orders-col-actions">
                    <span class="order-status" :class="`status-${order.status || 'unpaid'}`">
                      {{ $t(`account_modal.order_status.${order.status}`) }}
                    </span>
                    <button type="button" class="order-view-btn" @click="viewOrder(order)">
                      {{ $t('account_modal.orders_page.view') }}
                    </button>
                  </div>
                </div>
              </div>
              <Pagination
                :current-page="orderCurrentPage"
                :total-items="orderTotalCount"
                :page-size="orderPageSize"
                @page-change="handleOrderPageChange"
              />
            </div>
          </div>

          <div v-else-if="activeTab === 'score_orders'" class="account-orders-page">
            <div class="orders-section">
              <div class="list-section-header">
                <span class="list-section-icon" v-html="activeTabIcon"></span>
                <h3 class="list-section-title">{{ $t('account_modal.score_orders_page.title') }}</h3>
              </div>
              <div class="list-divider"></div>

              <div v-if="!apiScoreOrders || apiScoreOrders.length === 0" class="list-empty">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 12 20 22 4 22 4 12"/>
                  <rect x="2" y="7" width="20" height="5"/>
                  <line x1="12" y1="22" x2="12" y2="7"/>
                  <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
                  <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
                </svg>
                <p class="empty-text">{{ $t('account_modal.score_orders_page.no_orders') }}</p>
                <p class="empty-hint">{{ $t('account_modal.score_orders_page.hint') }}</p>
              </div>

              <div v-else class="orders-table score-orders-table">
                <div class="orders-table-head">
                  <span class="orders-col">{{ $t('account_modal.score_orders_page.product') }}</span>
                  <span class="orders-col">{{ $t('account_modal.score_orders_page.order_number') }}</span>
                  <span class="orders-col">{{ $t('account_modal.score_orders_page.points') }}</span>
                  <span class="orders-col score-orders-col-status">{{ $t('account_modal.score_orders_page.status') }}</span>
                </div>
                <div
                  v-for="order in apiScoreOrders"
                  :key="order.id"
                  class="orders-table-row"
                >
                  <div class="orders-col score-order-product">
                    <img
                      v-if="order.goodsimageUrl"
                      :src="order.goodsimageUrl"
                      :alt="order.goodstitle"
                      class="score-order-img"
                    />
                    <span v-else class="score-order-img score-order-img-placeholder"></span>
                    <div class="score-order-title">
                      <p class="order-number">{{ isEn ? (order.goodstitle_en || order.goodstitle) : order.goodstitle }}</p>
                      <p class="order-date">&times;{{ order.counts || 1 }}</p>
                    </div>
                  </div>
                  <div class="orders-col orders-col-id">
                    <p class="order-number">{{ order.bianhao }}</p>
                    <p class="order-date">{{ formatOrderDate(order.createtime_text) }}</p>
                  </div>
                  <div class="orders-col">
                    <span class="order-total">
                      <span class="order-total-amount">{{ order.score }}</span> {{ $t('redemption_modal.points') }}
                    </span>
                  </div>
                  <div class="orders-col orders-col-actions score-order-status-col">
                    <span class="order-status" :class="`status-${order.status}`">
                      {{ $t(`account_modal.score_order_status.${order.status}`) }}
                    </span>
                    <p v-if="order.expressno" class="score-order-express">
                      {{ $t('account_modal.score_orders_page.express_no') }}: {{ order.expressno }}
                    </p>
                  </div>
                </div>
              </div>
              <Pagination
                :current-page="scoreOrderCurrentPage"
                :total-items="scoreOrderTotalCount"
                :page-size="scoreOrderPageSize"
                @page-change="handleScoreOrderPageChange"
              />
            </div>
          </div>

          <div v-else-if="isListTab" class="account-list-page">
            <!-- <div class="list-toolbar"> -->
              <!-- <span class="profile-meta">{{ clubPoints }} {{ $t('timeclub.points') }} / {{ $t('account_modal.profile_form.language') }}: {{ memberLevel }}</span> -->
            <!-- </div> -->

            <!-- 零售及小票 -->
            <div v-if="activeTab === 'receipts'" class="receipts-section">
              <div v-if="userReceipts.length === 0" class="list-empty">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
                <p class="empty-text">{{ $t('account_modal.receipts_page.no_receipts') }}</p>
                <p class="empty-hint">{{ $t('account_modal.receipts_page.hint') }}</p>
              </div>

              <div v-else class="receipts-grid">
                <div
                  v-for="receipt in userReceipts"
                  :key="receipt.id"
                  class="receipt-card"
                >
                  <div class="receipt-header">
                    <span class="receipt-store">{{ isEn ? (receipt.storename_en || receipt.storename) : receipt.storename }}</span>
                    <span class="receipt-date">{{ formatReceiptDate(receipt.paytime) }}</span>
                  </div>
                  <div class="receipt-body">
                    <div class="receipt-info">
                      <span class="receipt-number">{{ $t('account_modal.receipts_page.receipt_number') }}: {{ receipt.bianhao }}</span>
                      <span class="receipt-amount">HK${{ formatOrderAmount(receipt.money) }}</span>
                    </div>
                    <div class="receipt-points">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 6v12M8 10h8M8 14h8"/>
                      </svg>
                      +{{ receipt.getscore }} {{ $t('timeclub.points') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 我的優惠券 -->
            <div v-if="activeTab === 'coupons'" class="coupons-section">
              <!-- <div class="list-section-header">
                <span class="list-section-icon" v-html="activeTabIcon"></span>
                <h3 class="list-section-title">{{ $t('account_modal.coupons') }}</h3>
              </div>
              <div class="list-divider"></div> -->

              <div class="coupons-tabs">
                <button
                  :class="['coupons-tab', { active: couponFilter === 'available' }]"
                  @click="couponFilter = 'available'"
                >
                  {{ $t('account_modal.coupons_page.available') }} ({{ availableCoupons.length }})
                </button>
                <button
                  :class="['coupons-tab', { active: couponFilter === 'used' }]"
                  @click="couponFilter = 'used'"
                >
                  {{ $t('account_modal.coupons_page.used') }} ({{ usedCoupons.length }})
                </button>
                <button
                  :class="['coupons-tab', { active: couponFilter === 'expired' }]"
                  @click="couponFilter = 'expired'"
                >
                  {{ $t('account_modal.coupons_page.expired') }} ({{ expiredCoupons.length }})
                </button>
              </div>

              <div v-if="filteredCoupons.length === 0" class="list-empty">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                  <line x1="7" y1="7" x2="7.01" y2="7"/>
                </svg>
                <p class="empty-text">{{ $t('account_modal.coupons_page.no_coupons') }}</p>
                <p class="empty-hint">{{ $t('account_modal.coupons_page.hint') }}</p>
              </div>

              <div v-else class="coupons-grid">
                <div
                  v-for="coupon in filteredCoupons"
                  :key="coupon.id"
                  class="coupon-card"
                  :class="{ 
                    disabled: coupon.status === 'used' || coupon.status === 'expired',
                    expired: coupon.status === 'expired',
                    'coupon-free-shipping': coupon.coupon_type === '4'
                  }"
                >
                  <div class="coupon-left">
                    <!-- 免运费券显示 -->
                    <template v-if="coupon.coupon_type === '4'">
                      <div class="coupon-free-shipping-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                          <rect x="1" y="3" width="15" height="13"/>
                          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                          <circle cx="5.5" cy="18.5" r="2.5"/>
                          <circle cx="18.5" cy="18.5" r="2.5"/>
                        </svg>
                      </div>
                      <div class="coupon-free-shipping-text">{{ $t('account_modal.coupons_page.free_shipping') }}</div>
                    </template>
                    <!-- 普通优惠券显示金额 -->
                    <template v-else>
                      <div class="coupon-value-large">
                        <span class="coupon-currency-small">$</span>
                        <span class="coupon-amount-large">{{ coupon.value }}</span>
                      </div>
                      <div v-if="coupon.minSpend > 0" class="coupon-condition">{{ $t('account_modal.coupons_page.min_spend', { amount: coupon.minSpend }) }}</div>
                      <div v-else class="coupon-condition">{{ $t('account_modal.coupons_page.no_threshold') }}</div>
                    </template>
                  </div>
                  <div class="coupon-right">
                    <div class="coupon-details">
                      <div class="coupon-title-row">
                        <h4 class="coupon-title">{{ coupon.title }}</h4>
                        <!-- 优惠券类型标签 -->
                        <span v-if="coupon.coupon_type === '1'" class="coupon-type-badge coupon-type-universal">{{ $t('account_modal.coupons_page.type_universal') }}</span>
                        <span v-else-if="coupon.coupon_type === '2'" class="coupon-type-badge coupon-type-product">{{ $t('account_modal.coupons_page.type_specific_product') }}</span>
                        <span v-else-if="coupon.coupon_type === '3'" class="coupon-type-badge coupon-type-brand">{{ $t('account_modal.coupons_page.type_specific_brand') }}</span>
                        <span v-else-if="coupon.coupon_type === '4'" class="coupon-type-badge coupon-type-shipping">{{ $t('account_modal.coupons_page.type_free_shipping') }}</span>
                      </div>
                      <p class="coupon-description">{{ coupon.description }}</p>
                      <!-- 特定商品显示适用商品 -->
                      <p v-if="coupon.coupon_type === '2' && coupon.goodsinfo && coupon.goodsinfo.length > 0" class="coupon-applicable">
                        <span class="applicable-label">{{ $t('account_modal.coupons_page.applicable_products') }}:</span>
                        <span class="applicable-value">{{ formatInfoList(coupon.goodsinfo) }}</span>
                      </p>
                      <!-- 特定品牌显示适用品牌 -->
                      <p v-if="coupon.coupon_type === '3' && coupon.brandinfo && coupon.brandinfo.length > 0" class="coupon-applicable">
                        <span class="applicable-label">{{ $t('account_modal.coupons_page.applicable_brands') }}:</span>
                        <span class="applicable-value">{{ formatInfoList(coupon.brandinfo) }}</span>
                      </p>
                      <div class="coupon-validity">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        {{ $t('account_modal.coupons_page.valid_until') }} {{ formatCouponDate(coupon.expiryDate) }}
                      </div>
                    </div>
                    <div class="coupon-status">
                      <span v-if="coupon.status === 'available'" class="status-badge status-available">{{ $t('account_modal.coupons_page.available') }}</span>
                      <span v-else-if="coupon.status === 'used'" class="status-badge status-used">{{ $t('account_modal.coupons_page.used') }}</span>
                      <span v-else-if="coupon.status === 'expired'" class="status-badge status-expired">{{ $t('account_modal.coupons_page.expired') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <!-- 訂單詳情 -->
      <transition name="modal-fade">
        <div v-if="selectedOrder" class="order-detail-overlay" @click.self="closeOrderDetail">
          <div class="order-detail-modal">
            <button class="order-detail-close" aria-label="關閉" @click="closeOrderDetail">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>

            <div v-if="orderDetailLoading" class="order-detail-loading">
              <span class="loading-spinner"></span>
            </div>
            <template v-else>

            <div class="order-detail-scroll">

              <div class="order-detail-header">
                <div class="order-detail-id-wrap">
                  <span class="order-detail-id">{{ selectedOrder.orderNumber || selectedOrder.id }}</span>
                  <span class="status-badge" :class="selectedOrder.status">{{ $t(`account_modal.order_status.${selectedOrder.status}`) }}</span>
                </div>
                <!-- 支付方式：1=香港钱包支付，2=大陆钱包支付，3=USDT支付，4=银联支付 -->
                <div class="order-detail-actions" v-if="selectedOrder.status === 'unpaid'">
                  <button
                    type="button"
                    class="order-cancel-btn"
                    @click="handleCancelOrder"
                  >
                    {{ $t('account_modal.order_detail.cancel_order') }}
                  </button>
                  <div class="order-pay-methods">
                    <button
                      type="button"
                      class="order-pay-btn"
                      @click="handlePayOrder('alipay')"
                      v-if="selectedOrder.payway==2||selectedOrder.payway==1"
                    >
                      {{ $t('cart_modal.payment_options.alipay') || '支付宝支付' }}
                    </button>
                    <button
                      type="button"
                      class="order-pay-btn order-pay-btn-chinapay"
                      @click="handlePayOrder('chinapay')"
                      v-if="selectedOrder.payway==4"
                    >
                      {{ $t('cart_modal.payment_options.chinapay') || '银联支付' }}
                    </button>
                  </div>
                </div>
              </div>

              <div class="order-detail-section">
                <div class="order-section-title">{{ $t('account_modal.order_detail.timeline') }}</div>
                <ul class="order-timeline">
                  <li class="order-timeline-item">
                    <span class="order-timeline-dot" :class="{ active: selectedOrder.date }"></span>
                    <span class="order-timeline-label">{{ $t('account_modal.orders_page.order_date') }}</span>
                    <span class="order-timeline-value">{{ formatOrderDate(selectedOrder.date) || '--' }}</span>
                  </li>
                  <li class="order-timeline-item" v-if="selectedOrder.paytime">
                    <span class="order-timeline-dot active"></span>
                    <span class="order-timeline-label">{{ $t('account_modal.order_detail.pay_time') }}</span>
                    <span class="order-timeline-value">{{ formatOrderDate(selectedOrder.paytime) }}</span>
                  </li>
                  <li class="order-timeline-item" v-if="selectedOrder.sendtime">
                    <span class="order-timeline-dot active"></span>
                    <span class="order-timeline-label">{{ $t('account_modal.order_detail.send_time') }}</span>
                    <span class="order-timeline-value">{{ formatOrderDate(selectedOrder.sendtime) }}</span>
                  </li>
                  <li class="order-timeline-item" v-if="selectedOrder.finishtime">
                    <span class="order-timeline-dot active"></span>
                    <span class="order-timeline-label">{{ $t('account_modal.order_detail.finish_time') }}</span>
                    <span class="order-timeline-value">{{ formatOrderDate(selectedOrder.finishtime) }}</span>
                  </li>
                </ul>
              </div>

              <div class="order-detail-section">
                <div class="order-section-title">{{ $t('account_modal.order_detail.amount_breakdown') }}</div>
                <div class="order-amount-list">
                  <div class="order-amount-row">
                    <span class="order-amount-label">{{ $t('cart.subtotal') }}</span>
                    <span class="order-amount-value">${{ formatOrderAmount(selectedOrder.subtotal) }}</span>
                  </div>
                  <div class="order-amount-row" v-if="amountGt0(selectedOrder.discount)">
                    <span class="order-amount-label">{{ $t('cart_modal.summary.discount') }}</span>
                    <span class="order-amount-value order-amount-discount">-USD ${{ formatOrderAmount(selectedOrder.discount) }}</span>
                  </div>
                  <div class="order-amount-row">
                    <span class="order-amount-label">{{ $t('account_modal.order_detail.freight') }}</span>
                    <span class="order-amount-value">USD ${{ formatOrderAmount(selectedOrder.freight) }}</span>
                  </div>
                  <div class="order-amount-row order-amount-row-total">
                    <span class="order-amount-label">{{ $t('account_modal.orders_page.order_total') }}</span>
                    <span class="order-amount-value order-amount-value-gold">USD ${{ formatOrderAmount(selectedOrder.total) }}</span>
                  </div>
                  <div class="order-amount-row" v-if="amountGt0(selectedOrder.paymoney)">
                    <span class="order-amount-label">{{ $t('account_modal.order_detail.paid_amount') }}</span>
                    <span class="order-amount-value order-amount-paid">{{ selectedOrder.payway == 4 ? 'USD':'HKD' }} ${{ formatOrderAmount(selectedOrder.paymoney) }}</span>
                  </div>
                </div>
              </div>

              <div class="order-detail-section">
                <div class="order-section-title">{{ $t('account_modal.order_detail.recipient_info') }}</div>
                <div class="order-recipient-card">
                  <div class="order-recipient-row">
                    <span class="order-recipient-label">{{ $t('account_modal.profile_form.name') }}</span>
                    <span class="order-recipient-value">{{ selectedOrder.recipient && selectedOrder.recipient.name || '--' }}</span>
                  </div>
                  <div class="order-recipient-row">
                    <span class="order-recipient-label">{{ $t('account_modal.profile_form.phone_number') }}</span>
                    <span class="order-recipient-value">{{ selectedOrder.recipient && selectedOrder.recipient.phone || '--' }}</span>
                  </div>
                  <div class="order-recipient-row">
                    <span class="order-recipient-label">{{ $t('account_modal.profile_form.address') }}</span>
                    <span class="order-recipient-value">{{ selectedOrder.recipient && selectedOrder.recipient.address || '--' }}</span>
                  </div>
                </div>
              </div>

              <div class="order-detail-section">
                <div class="order-section-title">{{ $t('account_modal.order_detail.items') }}<span class="order-section-count">({{ selectedOrder.counts || (selectedOrder.items || []).length }})</span></div>
                <div class="order-items-list">
                  <div
                    v-for="(item, idx) in selectedOrder.items || []"
                    :key="idx"
                    class="order-item-card"
                  >
                    <div class="order-item-image-wrap">
                      <img
                        v-if="item.image"
                        :src="item.image"
                        :alt="item.name"
                        class="order-item-image"
                      />
                      <span v-else class="order-item-image-placeholder"></span>
                    </div>
                    <div class="order-item-info">
                      <p v-if="item.brand" class="order-item-brand">{{ item.brand }}</p>
                      <p class="order-item-name">{{ item.name }}</p>
                      <p v-if="item.spec" class="order-item-spec">{{ $t('account_modal.order_detail.spec') }}: {{ item.spec }}</p>
                      <div class="order-item-price-row">
                        <span class="order-item-unit-price">USD ${{ formatOrderAmount(item.unitPrice) }}</span>
                        <span v-if="item.originalPrice" class="order-item-original-price">{{ selectedOrder.payway == 4 ? 'USD':'HKD' }} ${{ formatOrderAmount(item.originalPrice) }}</span>
                      </div>
                    </div>
                    <div class="order-item-qty-col">
                      <span class="order-item-qty-label">{{ $t('account_modal.order_detail.quantity') }}</span>
                      <span class="order-item-qty">×{{ item.quantity }}</span>
                    </div>
                    <div class="order-item-line-total-col">
                      <span class="order-item-line-total">USD ${{ formatOrderAmount(item.lineTotal) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="order-detail-section"
                v-if="selectedOrder.remark || selectedOrder.expressno"
              >
                <div class="order-section-title">{{ $t('account_modal.order_detail.order_info') }}</div>
                <div class="order-info-card">
                  <div class="order-info-row" v-if="selectedOrder.remark">
                    <span class="order-info-label">{{ $t('account_modal.order_detail.remark') }}</span>
                    <span class="order-info-value">{{ selectedOrder.remark }}</span>
                  </div>
                  <!-- 查看物流 -->
                  <div class="order-info-row" v-if="selectedOrder.expressno">
                    <span class="order-info-label">{{ $t('account_modal.order_detail.express_no') }}</span>
                    <span class="order-info-value order-info-express">
                      <div
                        v-for="(no, idx) in expressnoList"
                        :key="idx"
                        class="express-item"
                      >
                        <span class="express-no">{{ no }}</span>
                        <a href="javascript:;" class="express-track-link" @click="openTracking(no)">
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                          {{ $t('account_modal.order_detail.track_package') }}
                        </a>
                      </div>
                    </span>
                  </div>
                </div>
              </div>

            </div>
            </template>
          </div>
        </div>
      </transition>

      <!-- U盾轉賬提示彈窗 -->
      <transition name="modal-fade">
        <div v-if="showUShieldModal" class="ushield-modal-overlay" @click.self="showUShieldModal = false">
          <div class="ushield-modal-container">
            <div class="ushield-modal-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <polyline points="9 12 11 14 15 10"/>
              </svg>
            </div>
            <p class="ushield-modal-text">{{ $t('cart_modal.u_shield_modal.text') }}</p>
            <button class="ushield-modal-btn" @click="confirmUShieldTransfer">{{ $t('cart_modal.u_shield_modal.confirm') }}</button>
          </div>
        </div>
      </transition>

      <!-- 支付寶/銀聯支付彈窗 -->
      <transition name="modal-fade">
        <div v-if="showAlipayModal" class="alipay-modal-overlay" @click.self="closeAlipayModal">
          <div :class="['alipay-modal-container', { 'chinapay-modal': isChinapayModal }]">
            <div class="alipay-modal-header">
              <h3 class="alipay-modal-title">{{ $t('cart_modal.payment') }}</h3>
              <button class="alipay-modal-close" @click="closeAlipayModal">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div class="alipay-modal-body">
              <template v-if="isChinapayModal">
                <div class="chinapay-waiting">
                  <div class="chinapay-spinner"></div>
                  <p class="chinapay-waiting-text">{{ $t('cart_modal.chinapay_waiting') || '请在新窗口完成支付，支付完成后将自动跳转' }}</p>
                </div>
              </template>
              <template v-else>
                <iframe name="alipayFrame" class="alipay-iframe" frameborder="0"></iframe>
              </template>
            </div>
            <div class="alipay-modal-footer">
              <button
                type="button"
                class="alipay-paid-btn"
                :disabled="manualPayChecking"
                @click="handleManualPaidCheck"
              >
                {{ manualPayChecking ? $t('cart_modal.payment_checking') : $t('cart_modal.i_have_paid') }}
              </button>
              <p class="alipay-paid-hint">{{ $t('cart_modal.i_have_paid_hint') }}</p>
            </div>
          </div>
        </div>
      </transition>

      <!-- 我的地址簿 -->
      <transition name="modal-fade">
        <div v-if="addressBookVisible" class="address-book-overlay" @click.self="closeAddressBook">
          <div class="address-book-modal">
            <button class="address-book-close" aria-label="關閉" @click="closeAddressBook">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>

            <div class="address-book-header">
              <h3 class="address-book-title">{{ $t('account_modal.address_book.title') }}</h3>
              <button
                type="button"
                class="address-book-add-btn"
                :disabled="userAddresses.length >= addressLimit"
                @click="openAddressForm()"
              >
                {{ $t('account_modal.address_book.add_new') }}
              </button>
            </div>

            <p class="address-book-limit">{{ $t('account_modal.address_book.limit', { count: addressLimit }) }}</p>

            <div v-if="userAddresses.length === 0" class="address-book-empty">
              <p>{{ $t('account_modal.address_book.no_address') }}</p>
            </div>

            <div v-else class="address-book-list">
              <div
                v-for="addr in userAddresses"
                :key="addr.id"
                class="address-book-item"
              >
                <div class="address-book-item-main">
                  <p class="address-book-name">{{ addressDisplayName(addr) }}</p>
                  <p class="address-book-phone">
                    <span class="address-book-phone-badge">{{ addressDisplayPhone(addr) }}</span>
                  </p>
                  <p
                    v-for="(line, idx) in addressDisplayLines(addr)"
                    :key="idx"
                    class="address-book-line"
                  >{{ line }}</p>
                </div>
                <div class="address-book-item-actions">
                  <button type="button" class="address-book-edit-btn" @click="openAddressForm(addr)">
                    {{ $t('account_modal.address_book.edit') }}
                  </button>
                  <button
                    type="button"
                    class="address-book-delete-btn"
                    aria-label="刪除"
                    @click="handleDeleteAddress(addr.id)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- 新增/修改地址 -->
      <transition name="modal-fade">
        <div v-if="addressFormVisible" class="address-form-overlay" @click.self="closeAddressForm">
          <div class="address-form-modal">
            <button class="address-form-close" aria-label="關閉" @click="closeAddressForm">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>

            <h3 class="address-form-title">{{ $t(`account_modal.address_form.${addressForm.id ? 'edit' : 'add'}`) }}</h3>

            <form class="address-form" @submit.prevent="handleSaveAddress">
              <div class="profile-field-row profile-field-row-name">
                <div class="profile-field profile-field-salutation">
                  <label class="profile-label">{{ $t('account_modal.address_form.salutation') }}</label>
                  <select v-model="addressForm.salutation" class="profile-select">
                    <option value="mr">{{ $t('account_modal.address_form.salutation_mr') }}</option>
                    <option value="mrs">{{ $t('account_modal.address_form.salutation_mrs') }}</option>
                    <option value="ms">{{ $t('account_modal.address_form.salutation_ms') }}</option>
                  </select>
                </div>
                <div class="profile-field">
                  <label class="profile-label">{{ $t('account_modal.address_form.name') }}</label>
                  <input v-model="addressForm.name" type="text" class="profile-input" />
                </div>
              </div>

              <div class="profile-field-row">
                <div class="profile-field profile-field-region">
                  <label class="profile-label">{{ $t('account_modal.address_form.mobile') }}</label>
                  <select v-model="addressForm.phoneRegion" class="profile-select">
                    <option value="+852">{{ $t('account_modal.address_form.phone_regions.hk') }}</option>
                    <option value="+86">{{ $t('account_modal.address_form.phone_regions.cn') }}</option>
                    <option value="+886">{{ $t('account_modal.address_form.phone_regions.tw') }}</option>
                    <option value="+853">{{ $t('account_modal.address_form.phone_regions.mo') }}</option>
                  </select>
                </div>
                <div class="profile-field profile-field-phone">
                  <label class="profile-label profile-label-hidden">{{ $t('account_modal.address_form.mobile') }}</label>
                  <input v-model="addressForm.phone" type="tel" class="profile-input" :placeholder="$t('account_modal.address_form.mobile')" />
                </div>
              </div>

              <div class="profile-field">
                <label class="profile-label">{{ $t('account_modal.address_form.address') }}</label>
                <textarea v-model="addressForm.address" class="profile-input address-form-textarea" rows="4" />
              </div>

              <div class="profile-field-row">
                <div class="profile-field">
                  <label class="profile-label profile-label-hidden">{{ $t('account_modal.address_form.region') }}</label>
                  <select v-model="addressForm.region" class="profile-select" @change="handleAddressRegionChange">
                    <option v-for="r in addressRegions" :key="r" :value="r">{{ $t(`account_modal.address_form.regions.${r}`) }}</option>
                  </select>
                </div>
                <div class="profile-field">
                  <label class="profile-label profile-label-hidden">{{ $t('account_modal.address_form.city') }}</label>
                  <select v-model="addressForm.city" class="profile-select">
                    <option v-for="c in addressCityOptions" :key="c" :value="c">{{ $t(`account_modal.address_form.cities.${c}`) }}</option>
                  </select>
                </div>
              </div>

              <div class="profile-field">
                <label class="profile-label">{{ $t('account_modal.address_form.postal_code') }}</label>
                <input v-model="addressForm.postalCode" type="text" class="profile-input" />
              </div>

              <button type="submit" class="address-form-save-btn">{{ $t('account_modal.address_form.save') }}</button>
            </form>
          </div>
        </div>
      </transition>

      <!-- 積分兌換弹窗 -->
      <RedemptionModal
        :visible="redemptionModalVisible"
        :userPoints="clubPoints"
        @close="closeRedemptionModal"
        @redeem="handleRedemption"
      />

      <!-- 規則說明弹窗 -->
      <PageModal
        :visible="ruleModalVisible"
        :title="ruleModalTitle"
        @close="ruleModalVisible = false"
      >
        <div v-if="ruleLoading" class="rule-modal-loading">
          <span class="loading-spinner"></span>
        </div>
        <div v-else class="rule-modal-content" v-html="ruleModalContent"></div>
      </PageModal>
    </div>
  </transition>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { TIME_CLUB_LOGO } from '@/constants/benefits'
import { getFullUrl } from '@/api/request'
import { goodsApi } from '@/api/goods'
import RedemptionModal from './RedemptionModal.vue'
import Pagination from './Pagination.vue'
import PageModal from './PageModal.vue'

const ADDRESS_LIMIT = 10

const ADDRESS_REGIONS = ['hong_kong', 'mainland', 'taiwan', 'macau']

const ADDRESS_CITIES = {
  'hong_kong': ['hk_island', 'kowloon', 'new_territories'],
  'mainland': ['beijing', 'shanghai', 'guangzhou', 'shenzhen'],
  'taiwan': ['taipei', 'new_taipei', 'taichung', 'kaohsiung'],
  'macau': ['macau_peninsula', 'taipa', 'coloane']
}

const EMPTY_ADDRESS_FORM = {
  id: null,
  salutation: 'mr',
  name: '',
  phoneRegion: '+86',
  phone: '',
  address: '',
  region: 'hong_kong',
  city: 'hk_island',
  postalCode: ''
}

const NAV_ITEMS = [
  {
    id: 'overview',
    labelKey: 'account_modal.overview',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>'
  },
  {
    id: 'profile',
    labelKey: 'account_modal.profile',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>'
  },
  {
    id: 'orders',
    labelKey: 'account_modal.orders',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>'
  },
  {
    id: 'score_orders',
    labelKey: 'account_modal.score_orders',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>'
  },
  // {
  //   id: 'receipts',
  //   labelKey: 'account_modal.receipts',
  //   icon: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>'
  // },
  {
    id: 'coupons',
    labelKey: 'account_modal.coupons',
    icon: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>'
  }
]

export default {
  name: 'AccountOverviewModal',
  components: {
    RedemptionModal,
    Pagination,
    PageModal
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialTab: {
      type: String,
      default: 'overview'
    }
  },
  data() {
    const currentYear = new Date().getFullYear()
    return {
      timeClubLogo: TIME_CLUB_LOGO,
      navItems: NAV_ITEMS,
      activeTab: 'overview',
      memberSpending: 0,
      upgradeTarget: 0,
      clubPoints: 0,
      birthYears: Array.from({ length: 80 }, (_, i) => currentYear - 18 - i),
      showProfilePassword: false,
      showProfileConfirmPassword: false,
      profileForm: {
        email: '',
        salutation: 'mr',
        name: '',
        phoneRegion: '+86',
        phone: '',
        address: '',
        password: '',
        confirmPassword: '',
        birthYear: 2001,
        birthMonth: 7,
        birthDay: 3,
        language: '',
        marketingConsent: false,
        weixin: ''
      },
      selectedOrder: null,
      showUShieldModal: false,
      showAlipayModal: false,
      isChinapayModal: false,
      manualPayChecking: false,
      payQueryOrderNumber: '',
      payTimer: null,
      nextPayMoney: 0,
      addressLimit: ADDRESS_LIMIT,
      addressRegions: ADDRESS_REGIONS,
      addressBookVisible: false,
      addressFormVisible: false,
      addressForm: { ...EMPTY_ADDRESS_FORM },
      redemptionModalVisible: false,
      couponFilter: 'available',
      apiReceipts: null,
      apiCoupons: null,
      apiAddresses: null,
      apiOrders: null,
      orderDetailLoading: false,
      orderCurrentPage: 1,
      orderTotalCount: 0,
      orderPageSize: 10,
      apiScoreOrders: null,
      scoreOrderCurrentPage: 1,
      scoreOrderTotalCount: 0,
      scoreOrderPageSize: 10,
      ruleModalVisible: false,
      ruleModalTitle: '',
      ruleModalContent: '',
      ruleLoading: false,
      chinapayWindow: null
    }
  },
  computed: {
    ...mapState(['user']),
    isEn() {
      return this.$i18n.locale === 'en'
    },
    expressnoList() {
      if (!this.selectedOrder || !this.selectedOrder.expressno) return []
      return String(this.selectedOrder.expressno)
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    },
    memberLevel() {
      const rank = this.user && this.user.nowrank
      if (rank && rank.name) return rank.name
      return (this.user && this.user.memberLevel) || '--'
    },
    memberLevelText() {
      const rank = this.user && this.user.nowrank
      if (rank && rank.name) {
        const suffix = this.$i18n.locale === 'en' ? ' Member' : ' - 当前會員 - '
        const info = this.$i18n.locale === 'en' ? (rank.info_en || '') : (rank.info || '')
        return rank.name + suffix + info
      }
      return this.$t('account_modal.member_card.level')
    },
    discountHint() {
      // 優先顯示下一級會員折扣；若無下一級則顯示當前等級折扣
      const rank = (this.user && this.user.nextrank) || (this.user && this.user.nowrank)
      if (rank && rank.discount) {
        const discount = parseFloat(rank.discount)
        if (!isNaN(discount)) {
          if (this.$i18n.locale === 'en') {
            const percentOff = Math.round((10 - discount) * 10)
            return percentOff > 0 ? percentOff + '% Off' : 'No Discount'
          }
          const zhe = discount % 1 === 0 ? discount.toFixed(0) : discount.toFixed(1)
          return zhe + '折'
        }
      }
      return this.$t('account_modal.member_card.discount_hint')
    },
    displayName() {
      if (!this.user) return this.$t('account_modal.member_card.level')
      const genderReverseMap = { 1: 'mr', 2: 'mrs', 3: 'ms', '1': 'mr', '2': 'mrs', '3': 'ms' }
      const salutationMap = {
        mr: this.$t('account_modal.profile_form.salutation_mr'),
        mrs: this.$t('account_modal.profile_form.salutation_mrs'),
        ms: this.$t('account_modal.profile_form.salutation_ms')
      }
      const salutationKey = this.user.salutation || genderReverseMap[this.user.gender] || ''
      const salutationText = salutationMap[salutationKey] || ''
      let fullName = ''
      if (this.user.surname && this.user.name) {
        fullName = `${this.user.surname}${this.user.name}`
      } else if (this.user.lastName && this.user.firstName) {
        fullName = `${this.user.lastName}${this.user.firstName}`
      } else {
        fullName = this.user.name || this.user.firstName || ''
      }
      if (salutationText && fullName) {
        return `${salutationText} ${fullName}`
      }
      return fullName || this.user.email || this.$t('account_modal.member_card.level')
    },
    hasNextRank() {
      const nextrank = this.user && this.user.nextrank
      return !!(nextrank && nextrank.name)
    },
    nextRankName() {
      const nextrank = this.user && this.user.nextrank
      return (nextrank && nextrank.name) || ''
    },
    memberUpgradeStatus() {
      if (!this.hasNextRank) return 'max'
      const remaining = parseFloat(this.nextPayMoney)
      if (!isNaN(remaining) && remaining <= 0) return 'ready'
      return 'spending'
    },
    progressPercent() {
      if (!this.hasNextRank) return 100
      if (!this.upgradeTarget) return 0
      const pct = (this.memberSpending / this.upgradeTarget) * 100
      return Math.min(100, Math.max(0, pct))
    },
    spendingToUpgrade() {
      if (this.memberUpgradeStatus !== 'spending') return null
      // 直接使用后端返回的 nextpaymoney（距下一级还需消费），避免与 shengji 门槛累计计算产生偏差
      return Number(this.nextPayMoney).toFixed(2)
    },
    activeTabLabel() {
      const item = this.navItems.find(nav => nav.id === this.activeTab)
      return item ? this.$t(item.labelKey) : this.$t('account_modal.overview')
    },
    isListTab() {
      return ['receipts', 'coupons'].includes(this.activeTab)
    },
    activeTabIcon() {
      const item = this.navItems.find(nav => nav.id === this.activeTab)
      return item ? item.icon : ''
    },
    userOrders() {
      if (this.apiOrders) return this.apiOrders.map(o => this.mapApiOrder(o))
      return (this.user && this.user.orders) || []
    },
    userReceipts() {
      if (this.apiReceipts) return this.apiReceipts
      return (this.user && this.user.receipts) || []
    },
    userCoupons() {
      if (this.apiCoupons) return this.apiCoupons
      return (this.user && this.user.coupons) || []
    },
    availableCoupons() {
      return this.userCoupons.filter(c => c.status === 'available')
    },
    usedCoupons() {
      return this.userCoupons.filter(c => c.status === 'used')
    },
    expiredCoupons() {
      return this.userCoupons.filter(c => c.status === 'expired')
    },
    filteredCoupons() {
      if (this.couponFilter === 'available') return this.availableCoupons
      if (this.couponFilter === 'used') return this.usedCoupons
      if (this.couponFilter === 'expired') return this.expiredCoupons
      return this.userCoupons
    },
    couponFilterLabel() {
      return this.$t(`account_modal.coupons_page.${this.couponFilter}`)
    },
    activeListItems() {
      if (this.activeTab === 'receipts') return this.userReceipts
      if (this.activeTab === 'coupons') return this.userCoupons
      return []
    },
    userAddresses() {
      if (this.apiAddresses) return this.apiAddresses
      return (this.user && this.user.addresses) || []
    },
    addressFormTitle() {
      return this.$t(`account_modal.address_form.${this.addressForm.id ? 'edit' : 'add'}`)
    },
    addressCityOptions() {
      return ADDRESS_CITIES[this.addressForm.region] || []
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.activeTab = this.initialTab
        this.syncUserStats()
        if (this.activeTab === 'profile') {
          this.initProfileForm()
        }
        document.body.style.overflow = 'hidden'
        this.loadApiData()
      } else {
        this.selectedOrder = null
        this.addressBookVisible = false
        this.addressFormVisible = false
        this.redemptionModalVisible = false
        document.body.style.overflow = ''
      }
    },
    initialTab(val) {
      if (this.visible) {
        this.activeTab = val
        if (val === 'profile') {
          this.initProfileForm()
        }
      }
    },
    activeTab(val) {
      if (val === 'profile') {
        this.initProfileForm()
      }
    },
    couponFilter: {
      async handler() {
        await this.loadCoupons()
      }
    },
    user: {
      handler() {
        this.syncUserStats()
        if (this.visible && this.activeTab === 'profile') {
          this.initProfileForm()
        }
      },
      deep: true
    }
  },
  beforeDestroy() {
    this.stopPayQuery()
    // 清理可能打开的银联支付窗口
    if (this.chinapayWindow && !this.chinapayWindow.closed) {
      this.chinapayWindow.close()
      this.chinapayWindow = null
    }
  },
  methods: {
    ...mapActions(['closeAccountModal', 'logout', 'showToast', 'updateProfile', 'saveAddress', 'deleteAddress', 'fetchUserInfo', 'fetchCoupons', 'fetchReceipts', 'fetchAddresses', 'fetchOrders', 'fetchOrderDetail', 'fetchScoreGoods', 'fetchScoreOrders', 'exchangeGoods', 'exchangeCoupon']),
    handleClose() {
      this.closeAccountModal()
      this.$emit('close')
    },
    async handleTabClick(tabId) {
      this.activeTab = tabId
      //我的信息
      if (tabId === 'profile'||tabId === 'overview') {
        await this.fetchUserInfo()
      }
      if (tabId === 'orders') {
        //订单列表
        await this.fetchUserInfo()
        const [ordersRes] = await Promise.all([
          this.fetchOrders({ page: this.orderCurrentPage, count: this.orderPageSize })
        ])
        if (ordersRes && Array.isArray(ordersRes.list)) {
          this.apiOrders = ordersRes.list
          this.orderTotalCount = ordersRes.totalCount || 0
        }
      }
      if (tabId === 'score_orders') {
        this.scoreOrderCurrentPage = 1
        await this.loadScoreOrders(1)
      }
      if (tabId === 'coupons') {
        await this.fetchCoupons()
      }
    },
    handleLogout() {
      this.handleClose()
      this.logout()
      this.showToast({ message: this.$t('account_modal.logged_out'), type: 'success' })
    },
    goShopping() {
      this.handleClose()
      this.$router.push('/products')
    },
    syncUserStats() {
      if (!this.user) return
      this.clubPoints = parseFloat(this.user.score ?? this.user.points) || 0
      this.memberSpending = parseFloat(this.user.nowpaymoney) || 0
      this.nextPayMoney = parseFloat(this.user.nextpaymoney) || 0
      // nextrank.shengji = 下一级累计消费门槛；nextpaymoney = 距下一级还需消费
      const nextrank = this.user.nextrank
      if (nextrank && nextrank.shengji != null && nextrank.shengji !== '') {
        this.upgradeTarget = parseFloat(nextrank.shengji) || 0
      } else if (this.nextPayMoney > 0) {
        this.upgradeTarget = this.memberSpending + this.nextPayMoney
      } else {
        this.upgradeTarget = 0
      }
    },
    async loadApiData() {
      await this.fetchUserInfo()
      const [receipts, addresses, ordersRes] = await Promise.all([
        this.fetchReceipts(),
        this.fetchAddresses(),
        this.fetchOrders({ page: this.orderCurrentPage, count: this.orderPageSize })
      ])
      if (Array.isArray(receipts)) this.apiReceipts = receipts
      if (Array.isArray(addresses)) this.apiAddresses = addresses
      await this.loadCoupons()
      if (ordersRes && Array.isArray(ordersRes.list)) {
        this.apiOrders = ordersRes.list
        this.orderTotalCount = ordersRes.totalCount || 0
      }
    },
    async loadCoupons() {
      const statusMap = { available: 0, used: 1, expired: 2 }
      const status = statusMap[this.couponFilter]
      const cashCoupons = await this.fetchCoupons({ status })
      this.apiCoupons = Array.isArray(cashCoupons) ? cashCoupons : []
    },
    formatInfoList(list) {
      if (!Array.isArray(list) || list.length === 0) return ''
      const isEn = this.$i18n.locale === 'en'
      return list.map(item => isEn ? (item.name_en || item.title_en || item.name || item.title) : (item.name || item.title || item.name_en || item.title_en)).join(', ')
    },
    async handleOrderPageChange(page) {
      this.orderCurrentPage = page
      const res = await this.fetchOrders({ page: page, count: this.orderPageSize })
      if (res && Array.isArray(res.list)) {
        this.apiOrders = res.list
        this.orderTotalCount = res.totalCount || 0
      }
    },
    async loadScoreOrders(page = 1) {
      this.scoreOrderCurrentPage = page
      const res = await this.fetchScoreOrders({ page, count: this.scoreOrderPageSize })
      if (res && Array.isArray(res.list)) {
        this.apiScoreOrders = res.list.map(item => ({
          ...item,
          goodsimageUrl: item.goodsimage ? getFullUrl(item.goodsimage) : ''
        }))
        this.scoreOrderTotalCount = res.totalCount || 0
      } else {
        this.apiScoreOrders = []
        this.scoreOrderTotalCount = 0
      }
    },
    async handleScoreOrderPageChange(page) {
      await this.loadScoreOrders(page)
    },
    initProfileForm() {
      if (!this.user) return

      const phoneRegion = this.user.phoneRegion || '+86'
      let phone = this.user.phone || ''
      if (!this.user.phoneRegion && typeof phone === 'string' && phone.includes(' ')) {
        const parts = phone.split(' ')
        phone = parts.slice(1).join(' ')
      }

      // 获取姓名：优先使用name，否则拼接lastName和firstName
      let name = this.user.name || ''
      if (!name && (this.user.lastName || this.user.firstName)) {
        name = `${this.user.lastName || ''} ${this.user.firstName || ''}`.trim()
      }

      this.profileForm = {
        email: this.user.email || '',
        salutation: this.user.salutation || 'mr',
        name,
        phoneRegion,
        phone,
        address: this.user.address || '',
        password: '',
        confirmPassword: '',
        birthYear: this.user.birthYear || 2001,
        birthMonth: this.user.birthMonth || 1,
        birthDay: this.user.birthDay || 1,
        language: this.user.language || '',
        marketingConsent: !!this.user.marketingConsent,
        weixin: this.user.weixin || ''
      }
    },
    handleSaveProfile() {
      const {
        name,
        phone,
        password,
        confirmPassword,
        address,
        salutation,
        birthYear,
        birthMonth,
        birthDay,
        language
      } = this.profileForm
      const weixin = this.profileForm.weixin || ''
      
      if (!name || !phone || !address) {
        this.showToast({ message: this.$t('account_modal.validation.name_phone_required'), type: 'warning' })
        return
      }
      if (password || confirmPassword) {
        if (password !== confirmPassword) {
          this.showToast({ message: this.$t('account_modal.validation.password_mismatch'), type: 'warning' })
          return
        }
      }

      // 映射salutation到gender
      const genderMap = { mr: 1, mrs: 2, ms: 3 }
      const gender = genderMap[salutation] || 1

      // 格式化生日
      const birthday = `${birthYear}-${String(birthMonth).padStart(2, '0')}-${String(birthDay).padStart(2, '0')}`

      // 构建接口所需数据
      const profileData = {
        gender,
        name,
        phone,
        address,
        birthday,
        language,
        weixin
      }

      // 只有填写了密码才添加userpwd字段
      if (password) {
        profileData.userpwd = password
      }

      this.updateProfile(profileData)
      this.profileForm.password = ''
      this.profileForm.confirmPassword = ''
    },
    handleManageAddress() {
      this.addressBookVisible = true
    },
    closeAddressBook() {
      this.addressBookVisible = false
      this.addressFormVisible = false
    },
    openAddressForm(addr) {
      if (!addr) {
        if (this.userAddresses.length >= this.addressLimit) {
          this.showToast({ message: this.$t('account_modal.validation.address_limit', { count: this.addressLimit }), type: 'warning' })
          return
        }
        this.addressForm = {
          ...EMPTY_ADDRESS_FORM,
          name: this.profileForm.name,
          salutation: this.profileForm.salutation,
          phoneRegion: this.profileForm.phoneRegion,
          phone: this.profileForm.phone
        }
      } else {
        const region = ADDRESS_REGIONS.includes(addr.region) ? addr.region : 'hong_kong'
        const cities = ADDRESS_CITIES[region] || []
        const city = cities.includes(addr.city) ? addr.city : (cities[0] || '')
        // 获取姓名：优先使用name，否则拼接lastName和firstName
        let name = addr.name || ''
        if (!name && (addr.lastName || addr.firstName)) {
          name = `${addr.lastName || ''} ${addr.firstName || ''}`.trim()
        }
        this.addressForm = {
          id: addr.id,
          salutation: addr.salutation || 'mr',
          name,
          phoneRegion: addr.phoneRegion || '+852',
          phone: addr.phone || '',
          address: addr.address || '',
          region,
          city,
          postalCode: addr.postalCode || ''
        }
      }
      this.addressFormVisible = true
    },
    closeAddressForm() {
      this.addressFormVisible = false
      this.addressForm = { ...EMPTY_ADDRESS_FORM }
    },
    handleAddressRegionChange() {
      const cities = ADDRESS_CITIES[this.addressForm.region] || []
      if (!cities.includes(this.addressForm.city)) {
        this.addressForm.city = cities[0] || ''
      }
    },
    async handleSaveAddress() {
      const { name, phone, address } = this.addressForm
      if (!name || !phone || !address.trim()) {
        this.showToast({ message: this.$t('account_modal.validation.address_incomplete'), type: 'warning' })
        return
      }
      const saved = await this.saveAddress(this.addressForm)
      if (saved) {
        this.closeAddressForm()
        const addresses = await this.fetchAddresses()
        if (Array.isArray(addresses)) this.apiAddresses = addresses
      }
    },
    async handleDeleteAddress(id) {
      await this.deleteAddress(id)
      const addresses = await this.fetchAddresses()
      if (Array.isArray(addresses)) this.apiAddresses = addresses
    },
    addressDisplayName(addr) {
      // 获取姓名：优先使用name，否则拼接lastName和firstName
      let name = addr.name || ''
      if (!name && (addr.lastName || addr.firstName)) {
        name = `${addr.lastName || ''} ${addr.firstName || ''}`.trim()
      }
      const salutationMap = {
        mr: this.$t('account_modal.address_form.salutation_mr'),
        mrs: this.$t('account_modal.address_form.salutation_mrs'),
        ms: this.$t('account_modal.address_form.salutation_ms')
      }
      const salutationText = salutationMap[addr.salutation] || addr.salutation || ''
      if (name) {
        return salutationText ? `${name} ${salutationText}` : name
      }
      return '--'
    },
    addressDisplayPhone(addr) {
      const region = addr.phoneRegion || ''
      const phone = addr.phone || ''
      return phone ? `${region} ${phone}` : ''
    },
    addressDisplayLines(addr) {
      const lines = []
      if (addr.region) {
        const regionKey = `account_modal.address_form.regions.${addr.region}`
        const translated = this.$t(regionKey)
        lines.push(translated !== regionKey ? translated : addr.region)
      }
      if (addr.city) {
        const cityKey = `account_modal.address_form.cities.${addr.city}`
        const translated = this.$t(cityKey)
        lines.push(translated !== cityKey ? translated : addr.city)
      }
      if (addr.address) {
        String(addr.address).split('\n').forEach(line => {
          if (line.trim()) lines.push(line.trim())
        })
      }
      return lines
    },
    formatOrderDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      if (Number.isNaN(date.getTime())) return dateStr
      const y = date.getFullYear()
      const m = date.getMonth() + 1
      const d = date.getDate()
      const h = String(date.getHours()).padStart(2, '0')
      const min = String(date.getMinutes()).padStart(2, '0')
      return `${y}/${m}/${d}`
    },
    formatOrderAmount(num) {
      const value = parseFloat(num) || 0
      return value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    amountGt0(num) {
      return (parseFloat(num) || 0) > 0
    },
    orderStatusLabel(status) {
      return this.$t(`account_modal.order_status.${status}`)
    },
    orderRecipient(order) {
      let contact = order.recipient
      if (typeof contact === 'string') return contact
      if (!contact) contact = order.shipping || order.billing
      return this.orderContactName(contact)
    },
    orderContactName(contact) {
      if (!contact) return '--'
      return contact.name || '--'
    },
    orderContactPhone(contact) {
      if (!contact || !contact.phone) return ''
      return contact.phone
    },
    orderContactAddress(contact) {
      if (!contact || !contact.address) return []
      if (Array.isArray(contact.address)) return contact.address
      return String(contact.address).split('\n').filter(Boolean)
    },
    viewOrder(order) {
      this.selectedOrder = order
      if (order.apiId) {
        this.loadOrderDetail(order.apiId)
      }
    },
    async loadOrderDetail(orderId) {
      this.orderDetailLoading = true
      try {
        const detail = await this.fetchOrderDetail(orderId)
        if (detail) {
          this.selectedOrder = this.mapApiOrder(detail)
        }
      } catch (e) {
        console.error('获取订单详情失败', e)
      } finally {
        this.orderDetailLoading = false
      }
    },
    mapOrderStatus(apiStatus) {
      const map = { '0': 'unpaid', '1': 'paid', '2': 'shipping', '3': 'completed', '4': 'cancelled', '5': 'cancelled', '6': 'pending' }
      return map[String(apiStatus)] || 'unpaid'
    },
    mapApiOrder(o) {
      const isEn = this.$i18n.locale === 'en'
      const items = (o.cartinfo || []).map(c => {
        const gg = c.guigejson || {}
        const gi = c.goodsinfo || {}
        const unitPrice = gg.price || c.price
        const originalPrice = gg.original_price
        return {
          id: c.id,
          goodsId: c.goods_id,
          name: isEn ? (gi.title_en || gi.title) : (gi.title || gi.title_en),
          brand: '',
          image: getFullUrl(gi.image),
          spec: isEn ? (gg.guigename_en || gg.guigename) : (gg.guigename || gg.guigename_en),
          unitPrice,
          originalPrice: originalPrice && parseFloat(originalPrice) > parseFloat(unitPrice) ? originalPrice : null,
          quantity: c.counts,
          lineTotal: (parseFloat(unitPrice) * c.counts).toFixed(2),
          payway: o.payway
        }
      })
      const subtotal = items.reduce((sum, i) => sum + parseFloat(i.lineTotal), 0)
      const ts = (t) => t && t > 0 ? new Date(t * 1000).toISOString() : ''
      return {
        id: o.id,
        apiId: o.id,
        orderNumber: o.bianhao,
        date: ts(o.createtime),
        paytime: ts(o.paytime),
        sendtime: ts(o.sendtime),
        finishtime: ts(o.finishtime),
        status: this.mapOrderStatus(o.status),
        subtotal: subtotal.toFixed(2),
        discount: o.couponmoney || '0.00',
        freight: o.freight || '0.00',
        total: o.ordermoney || '0.00',
        paymoney: o.paymoney || '0.00',
        counts: o.counts || items.reduce((s, i) => s + i.quantity, 0),
        recipient: { name: o.name, phone: o.phone, address: o.address },
        items,
        expressno: o.expressno,
        remark: o.remark,
        payway: o.payway
      }
    },
    openTracking(expressno) {
      if (!expressno) return
      window.open('https://m.17track.net/zh-cn?v=2#nums=' + encodeURIComponent(expressno), '_blank')
    },
    closeOrderDetail() {
      this.selectedOrder = null
      //调取订单列表接口
      
    },
    async handlePayOrder(paymentMethod) {
      if (!this.selectedOrder || !this.selectedOrder.apiId) return
      const isChinapay = paymentMethod === 'chinapay'
      
      this.isChinapayModal = isChinapay
      try {
        const res = await goodsApi.payAgain({ envtype: 2, id: this.selectedOrder.apiId, ischinapay: isChinapay ? 1 : 0 })
        if (res.code === 1 && res.data && res.data.paramMap && res.data.paramMap.payUrl) {
          const payUrl = res.data.paramMap.payUrl
          // 覆盖原来的编号
          this.selectedOrder.orderNumber = res.data.bianhao
          this.payQueryOrderNumber = res.data.bianhao || ''
          
          // 银联支付：直接在当前页面提交表单（iOS兼容方案）
          if (isChinapay) {
            console.log('ChinaPay支付（订单继续支付）：准备在当前页面提交表单')
            
            // 查询支付订单状态 - 轮询
            if (this.selectedOrder.orderNumber) this.startPayQuery(this.selectedOrder.orderNumber)
            
            // 关闭新窗口（如果已打开）
            if (this.chinapayWindow && !this.chinapayWindow.closed) {
              this.chinapayWindow.close()
              this.chinapayWindow = null
            }
            
            // 先关闭所有模态框，清空状态
            this.showAlipayModal = false
            this.isChinapayModal = false
            
            // 使用$nextTick确保DOM更新完成后再提交表单
            this.$nextTick(() => {
              // 创建隐藏容器来解析表单HTML
              const container = document.createElement('div')
              container.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;overflow:hidden;'
              container.innerHTML = payUrl
              document.body.appendChild(container)
              
              const form = container.querySelector('form')
              if (form) {
                console.log('找到支付表单，表单action:', form.action, '方法:', form.method)
                // 确保表单配置正确
                form.removeAttribute('target')  // 移除target，在当前页面提交
                form.method = form.method || 'POST'
                
                // iOS Safari需要表单在DOM中可见才能正确提交
                form.style.display = 'block'
                
                // 立即提交表单（同步操作，避免异步延迟）
                console.log('提交支付表单...')
                try {
                  form.submit()
                } catch (submitError) {
                  console.error('表单提交失败:', submitError)
                  // 清理容器
                  if (container.parentNode) {
                    document.body.removeChild(container)
                  }
                  // 显示错误提示
                  this.showToast({ 
                    message: this.$t('cart_modal.payment_form_error') || '支付表单提交失败，请重试', 
                    type: 'error' 
                  })
                }
              } else {
                console.error('未找到支付表单，payUrl内容:', payUrl.substring(0, 200))
                // 清理容器
                if (container.parentNode) {
                  document.body.removeChild(container)
                }
                this.showToast({ 
                  message: this.$t('cart_modal.payment_form_error') || '支付表单加载失败，请重试', 
                  type: 'error' 
                })
              }
            })
          } else {
            // 支付宝支付：iframe 方式
            this.showAlipayModal = true
            if (this.selectedOrder.orderNumber) this.startPayQuery(this.selectedOrder.orderNumber)
            
            this.$nextTick(() => {
              const container = document.createElement('div')
              container.style.display = 'none'
              container.innerHTML = payUrl
              document.body.appendChild(container)
              const form = container.querySelector('form')
              if (form) {
                form.target = 'alipayFrame'
                form.submit()
              } else {
                const frame = document.querySelector('iframe[name="alipayFrame"]')
                if (frame) frame.src = payUrl
              }
              document.body.removeChild(container)
            })
          }
        } else {
          // 支付失败，关闭已打开的新窗口
          if (this.chinapayWindow && !this.chinapayWindow.closed) {
            this.chinapayWindow.close()
            this.chinapayWindow = null
          }
          this.showToast({ message: res.msg || this.$t('account_modal.order_detail.pay_failed'), type: 'error' })
        }
      } catch (e) {
        console.error('继续支付失败', e)
        // 支付失败，关闭已打开的新窗口
        if (this.chinapayWindow && !this.chinapayWindow.closed) {
          this.chinapayWindow.close()
          this.chinapayWindow = null
        }
        this.showToast({ message: this.$t('account_modal.order_detail.pay_failed'), type: 'error' })
      }
    },
    fallbackSubmitForm(payUrl, target) {
      console.log('使用回退方案提交支付表单')
      const container = document.createElement('div')
      container.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;'
      container.innerHTML = payUrl
      document.body.appendChild(container)
      const form = container.querySelector('form')
      if (form) {
        // 为 iOS 兼容性，确保表单有正确的属性
        form.target = target
        form.method = form.method || 'POST'
        form.submit()
        setTimeout(() => {
          if (container.parentNode) {
            container.parentNode.removeChild(container)
          }
        }, 5000)
      } else {
        console.error('回退方案中未找到表单')
        this.showToast({ 
          message: this.$t('cart_modal.payment_form_error') || '支付表单加载失败，请重试', 
          type: 'error' 
        })
      }
    },
    startPayQuery(bianhao) {
      this.stopPayQuery()
      this.payQueryOrderNumber = bianhao || this.payQueryOrderNumber
      setTimeout(async () => {
        const res = await this.fetchOrders({ page: this.orderCurrentPage, count: this.orderPageSize })
        if (res && Array.isArray(res.list)) {
          this.apiOrders = res.list
          this.orderTotalCount = res.totalCount || 0
        }
      }, 3000)
      this.payTimer = setInterval(async () => {
        try {
          const res = await goodsApi.payQuery({ bianhao })
          if (res.code === 1) {
            await this.handlePaySuccess()
          }
        } catch (e) {
          // 查询失败不停止轮询，等待下次重试
        }
      }, 2000)
      
    },
    async handlePaySuccess() {
      this.stopPayQuery()
      this.showAlipayModal = false
      this.closeOrderDetail()
      await this.handleOrderPageChange(this.orderCurrentPage)
      this.showToast({ message: this.$t('account_modal.order_detail.pay_success'), type: 'success' })
    },
    async handleManualPaidCheck() {
      if (this.manualPayChecking) return
      const bianhao = this.payQueryOrderNumber || (this.selectedOrder && this.selectedOrder.orderNumber)
      if (!bianhao) {
        this.showToast({ message: this.$t('cart_modal.payment_query_failed'), type: 'warning' })
        return
      }

      this.manualPayChecking = true
      try {
        const res = await goodsApi.payQuery({ bianhao })
        if (res.code === 1) {
          await this.handlePaySuccess()
          return
        }
        this.showToast({ message: res.msg || this.$t('cart_modal.payment_not_confirmed'), type: 'warning', duration: 4000 })
      } catch (e) {
        this.showToast({ message: this.$t('cart_modal.payment_query_failed'), type: 'error', duration: 4000 })
      } finally {
        this.manualPayChecking = false
      }
    },
    stopPayQuery() {
      if (this.payTimer) {
        clearInterval(this.payTimer)
        this.payTimer = null
      }
    },
    async handleCancelOrder() {
      if (!this.selectedOrder || !this.selectedOrder.apiId) return
      if (!confirm(this.$t('account_modal.order_detail.cancel_confirm'))) return
      try {
        const res = await goodsApi.cancelOrder({ id: this.selectedOrder.apiId })
        if (res.code === 1) {
          this.showToast({ message: this.$t('account_modal.order_detail.cancel_success'), type: 'success' })
          this.closeOrderDetail()
          await this.handleOrderPageChange(this.orderCurrentPage)
        } else {
          this.showToast({ message: res.msg || this.$t('account_modal.order_detail.cancel_failed'), type: 'error' })
        }
      } catch (e) {
        console.error('取消订单失败', e)
        this.showToast({ message: this.$t('account_modal.order_detail.cancel_failed'), type: 'error' })
      }
    },
    closeAlipayModal() {
      this.showAlipayModal = false
      this.manualPayChecking = false
    },
    confirmUShieldTransfer() {
      if (this.selectedOrder) {
        this.$store.commit('UPDATE_ORDER_STATUS', {
          orderId: this.selectedOrder.id,
          status: 'paid'
        })
      }
      this.showUShieldModal = false
      this.closeOrderDetail()
    },
    openRedemptionModal() {
      this.redemptionModalVisible = true
    },
    closeRedemptionModal() {
      this.redemptionModalVisible = false
    },
    async openRuleModal(type) {
      const isPoints = type === 'points'
      this.ruleModalTitle = this.$t(isPoints ? 'account_modal.rule.points_rule' : 'account_modal.rule.rank_rule')
      this.ruleModalVisible = true
      this.ruleLoading = true
      this.ruleModalContent = ''
      try {
        const res = await this.$api.home.getGuize()
        if (res.code === 1 && res.data) {
          const suffix = this.isEn ? '_en' : ''
          const raw = res.data[isPoints ? ('scoreguize' + suffix) : ('rankguize' + suffix)] || ''
          this.ruleModalContent = this.processRichText(raw)
        }
      } catch (e) {
        console.error('获取规则说明失败', e)
      } finally {
        this.ruleLoading = false
      }
    },
    processRichText(html) {
      if (!html) return ''
      return html
        .replace(/<img\s+([^>]*?)src=["'](?!https?:\/\/)([^"']+)["']/gi,
          (m, pre, src) => `<img ${pre}src="${getFullUrl(src)}"`)
        .replace(/<video\s+([^>]*?)src=["'](?!https?:\/\/)([^"']+)["']/gi,
          (m, pre, src) => `<video ${pre}src="${getFullUrl(src)}"`)
        .replace(/<source\s+([^>]*?)src=["'](?!https?:\/\/)([^"']+)["']/gi,
          (m, pre, src) => `<source ${pre}src="${getFullUrl(src)}"`)
        .replace(/poster=["'](?!https?:\/\/)([^"']+)["']/gi,
          (m, p) => `poster="${getFullUrl(p)}"`)
    },
    handleRedemption(item) {
      this.showToast({ 
        message: this.$t('account_modal.redemption_success', { name: item.name || item.brand, points: item.points }), 
        type: 'success' 
      })
      this.clubPoints = Math.max(0, this.clubPoints - item.points)
      this.closeRedemptionModal()
    },
    formatReceiptDate(dateStr) {
      if (!dateStr) return ''
      // Unix timestamp (秒)
      if (typeof dateStr === 'number' || /^\d{10}$/.test(dateStr)) {
        const d = new Date(Number(dateStr) * 1000)
        const y = d.getFullYear()
        const m = String(d.getMonth() + 1).padStart(2, '0')
        const day = String(d.getDate()).padStart(2, '0')
        return `${y}/${m}/${day}`
      }
      const date = new Date(dateStr)
      if (Number.isNaN(date.getTime())) return dateStr
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}/${m}/${d}`
    },
    formatCouponDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      if (Number.isNaN(date.getTime())) return dateStr
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}/${m}/${d}`
    }
  }
}
</script>

<style lang="scss" scoped>
.account-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.account-modal-container {
  display: flex;
  width: 100%;
  max-width: 1100px;
  height: 80vh;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.25);
}

.account-sidebar {
  width: 72px;
  flex-shrink: 0;
  background: #2a2a2a;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0 16px;
}

.sidebar-logo {
  width: 44px;
  margin-bottom: 28px;

  img {
    width: 100%;
    height: auto;
    display: block;
  }
}

.sidebar-nav {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.sidebar-nav-item {
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  color: #aaa;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;

  &:hover,
  &.active {
    background: rgba(255, 255, 255, 0.1);
    color: #d4b86a;
  }
}

.sidebar-nav-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.sidebar-logout {
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  color: #aaa;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 12px;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #d4b86a;
  }
}

.account-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.account-main-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px 16px;
  flex-shrink: 0;
}

.account-main-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.account-close-btn {
  width: 36px;
  height: 36px;
  background: #333;
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: #555;
  }
}

.account-overview {
  flex: 1;
  overflow-y: auto;
  padding: 0 28px 28px;
}

.overview-top {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.member-card {
  border-radius: 8px;
  overflow: hidden;
  background: linear-gradient(135deg, #c9a84c 0%, #e8d5a0 35%, #b8943f 70%, #d4b86a 100%);
  min-height: 220px;
}

.member-card-inner {
  padding: 28px 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  color: #3d3508;
}

.member-level {
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 8px;
}

.member-name {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 20px;
}

.member-spending {
  margin-bottom: 16px;

  .spending-label {
    display: block;
    font-size: 12px;
    opacity: 0.8;
    margin-bottom: 4px;
  }

  .spending-value {
    font-size: 18px;
    font-weight: 600;
  }
}

.member-progress {
  margin-top: auto;
}

.progress-track {
  position: relative;
  height: 8px;
  background: rgba(255, 255, 255, 0.35);
  border-radius: 4px;
  margin-bottom: 10px;
}

.progress-fill {
  height: 100%;
  background: #fff;
  border-radius: 4px;
  transition: width 0.3s;
}

.progress-lock {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #5a4a10;
  background: rgba(255, 255, 255, 0.6);
  padding: 2px 8px;
  border-radius: 10px;
}

.progress-hint {
  font-size: 12px;
  margin: 0;
  opacity: 0.85;
}

.member-rule-links {
  display: flex;
  gap: 16px;
  margin-top: 14px;
  flex-wrap: wrap;
}

.rule-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #3d3508;
  text-decoration: none;
  transition: color 0.3s;

  &:hover {
    color: #3d3508;
    text-decoration: underline;
  }

  svg {
    flex-shrink: 0;
  }
}

.overview-right {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.points-card {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e8e8e8;
  min-height: 220px;
  display: flex;
  flex-direction: column;
}

.points-card-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  background: linear-gradient(135deg, #b8934d 0%, #d4b86a 100%);
  opacity: 0.08;
}

.points-card-content {
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  text-align: center;
}

.rule-modal-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.rule-modal-content {
  color: #333;
  font-size: 14px;
  line-height: 1.8;

  ::v-deep {
    img {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
      margin: 12px 0;
    }

    p {
      margin: 12px 0;
    }

    h1, h2, h3, h4, h5, h6 {
      margin: 20px 0 12px;
      font-weight: 600;
    }

    a {
      color: #c8a45e;
      text-decoration: underline;
    }

    ul, ol {
      padding-left: 24px;
      margin: 12px 0;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 12px 0;

      td, th {
        border: 1px solid #e8e8e8;
        padding: 8px 12px;
      }
    }
  }
}

.points-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(135deg, #b8934d 0%, #d4b86a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  margin-bottom: 14px;
}

.points-label {
  font-size: 12px;
  color: #999;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.points-amount {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 6px;
}

.points-num {
  font-size: 40px;
  font-weight: 600;
  color: #b8934d;
  line-height: 1;
}

.points-unit {
  font-size: 13px;
  color: #999;
}

.points-tip {
  font-size: 12px;
  color: #bbb;
  margin: 0 0 20px;
  line-height: 1.5;
}

.points-redeem-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  background: #333;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  color: #fff;
  cursor: pointer;
  
  transition: background 0.2s, transform 0.2s;

  &:hover {
    background: #b8934d;
  }

  &:active {
    transform: scale(0.98);
  }
}

.overview-bottom {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.section-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
  min-height: 200px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  h3 {
    font-size: 15px;
    font-weight: 600;
    color: #333;
    margin: 0;
    
  }
}

.section-more {
  background: none;
  border: none;
  font-size: 13px;
  color: #999;
  cursor: pointer;
  padding: 0;
  

  &:hover {
    color: #987701;
  }
}

.section-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 0;
  color: #bbb;

  svg {
    margin-bottom: 12px;
    opacity: 0.5;
  }

  p {
    font-size: 14px;
    margin: 0 0 16px;
    color: #999;
    
  }
}

.section-action-btn {
  padding: 10px 28px;
  background: #fff;
  border: 1px solid #333;
  border-radius: 4px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: #333;
    color: #fff;
  }
}

.section-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.section-list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;

  &:hover {
    background: #f0f0f0;
    border-color: #ddd;
  }

  .item-info {
    flex: 1;
    min-width: 0;

    .item-title {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      margin: 0 0 4px;
      
    }

    .item-date {
      font-size: 12px;
      color: #999;
      margin: 0;
      
    }
  }

  .item-status {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;

    .status-badge {
      font-size: 11px;
      padding: 2px 8px;
      border-radius: 3px;
      

      &.unpaid {
        background: #fff3e0;
        color: #e65100;
      }

      &.paid {
        background: #e8f5e9;
        color: #2e7d32;
      }

      &.shipping {
        background: #e3f2fd;
        color: #1565c0;
      }

      &.pending {
        background: #fff8e1;
        color: #f57c00;
      }

      &.completed {
        background: #f3e5f5;
        color: #6a1b9a;
      }

      &.cancelled {
        background: #fce4ec;
        color: #c62828;
      }
    }

    .item-amount {
      font-size: 14px;
      font-weight: 600;
      color: #333;
      
    }

    .coupon-value {
      font-size: 16px;
      font-weight: 700;
      color: #c4a035;
      
    }
  }
}

.account-profile {
  flex: 1;
  overflow-y: auto;
  padding: 0 28px 28px;
}

.profile-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
}

.profile-meta {
  font-size: 14px;
  color: #666;
  
}

.profile-actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.profile-btn {
  padding: 10px 20px;
  font-size: 13px;
  border-radius: 4px;
  cursor: pointer;
  
  transition: background 0.2s, color 0.2s, border-color 0.2s;
  white-space: nowrap;
}

.profile-btn-outline {
  background: #fff;
  border: 1px solid #333;
  color: #333;

  &:hover {
    background: #f5f5f5;
  }
}

.profile-btn-primary {
  background: #3a3a3a;
  border: 1px solid #3a3a3a;
  color: #fff;

  &:hover {
    background: #2a2a2a;
  }
}

.profile-form {
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.profile-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.profile-field-full {
  max-width: 100%;
}

.profile-field-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.profile-field-row-name {
  .profile-field-salutation {
    flex: 0 0 100px;
  }
}

.profile-field-region {
  flex: 0 0 200px;
}

.profile-field-phone {
  flex: 1;
}

.profile-field-password {
  flex: 1;
}

.password-row {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input {
  padding-right: 44px !important;
}

.btn-toggle-password {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
  padding: 0;

  &:hover {
    color: #666;
  }

  &:focus {
    outline: none;
  }

  svg {
    display: block;
  }
}

.profile-field-row-inline {
  align-items: flex-start;

  .profile-field-birth {
    flex: 1.2;
  }

  .profile-field-lang {
    flex: 1;
  }
}

.profile-label {
  font-size: 13px;
  color: #666;
  
}

.profile-label-hidden {
  visibility: hidden;
}

.profile-input,
.profile-select {
  width: 100%;
  padding: 11px 14px;
  border: 1px solid #e0e0e0;
  background: #fff;
  font-size: 14px;
  color: #333;
  outline: none;
  box-sizing: border-box;
  
  transition: border-color 0.2s;

  &:focus {
    border-color: #8b7e55;
  }
}

.profile-input-readonly {
  background: #f8f8f8;
  color: #666;
  cursor: default;
}

.profile-date-group {
  display: flex;
  gap: 8px;

  .profile-select {
    flex: 1;
  }
}

.profile-consent {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: #666;
  line-height: 1.7;
  cursor: pointer;
  margin-top: 4px;
  

  input {
    margin-top: 4px;
    flex-shrink: 0;
    accent-color: #8b7e55;
  }
}

.account-list-page {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 28px 28px;
}

.list-toolbar {
  padding-bottom: 16px;
  margin-bottom: 8px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
}

.list-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.list-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0 16px;
  flex-shrink: 0;
}

.list-section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
}

.list-section-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0;
  
}

.list-divider {
  height: 1px;
  background: #e8e8e8;
  flex-shrink: 0;
}

.list-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;

  p {
    font-size: 15px;
    color: #999;
    margin: 0;
    
    font-weight: 300;
  }
}

.list-body {
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
}

.list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
}

.list-item-title {
  font-size: 14px;
  color: #333;
  margin: 0 0 4px;
  
}

.list-item-subtitle {
  font-size: 13px;
  color: #999;
  margin: 0;
  
}

.list-item-meta {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
  flex-shrink: 0;
}

.account-orders-page {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 28px 28px;
}

.orders-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.orders-table {
  flex: 1;
  overflow-y: auto;
  padding-top: 4px;
}

.orders-table-head {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 200px;
  gap: 16px;
  padding: 12px 0 16px;
  border-bottom: 1px solid #e8e8e8;
}

.orders-table-head .orders-col {
  font-size: 13px;
  color: #999;
  
}

.orders-table-row {
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 200px;
  gap: 16px;
  align-items: center;
  padding: 20px 0;
  border-bottom: 1px solid #f0f0f0;
}

.order-number {
  font-size: 15px;
  font-weight: 500;
  color: #333;
  margin: 0 0 6px;
  
}

.order-date {
  font-size: 13px;
  color: #999;
  margin: 0;
  
}

.order-recipient {
  font-size: 14px;
  color: #333;
  margin: 0;
  
}

.order-total {
  font-size: 14px;
  color: #333;
  
}

.order-total-amount {
  color: #b8934d;
  font-weight: 600;
}

.orders-col-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.order-status {
  font-size: 12px;
  color: #999;
  padding: 6px 14px;
  background: #f0f0f0;
  white-space: nowrap;
  

  &.status-unpaid {
    color: #c0392b;
    background: #fdf0ef;
  }

  &.status-paid {
    color: #27ae60;
    background: #eefdf3;
  }

  &.status-shipping {
    color: #2980b9;
    background: #eef5fb;
  }

  &.status-completed {
    color: #888;
    background: #f4f4f4;
  }

  &.status-cancelled {
    color: #999;
    background: #f0f0f0;
  }
}

.order-view-btn {
  padding: 8px 20px;
  background: #333;
  color: #fff;
  border: none;
  font-size: 13px;
  cursor: pointer;
  
  transition: background 0.2s;
  white-space: nowrap;

  &:hover {
    background: #555;
  }
}

.order-detail-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.order-detail-modal {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow: hidden;
  background: #f5f5f5;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.28);
}

.order-detail-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #ddd;
  border-top-color: #c0a062;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.order-detail-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 36px;
  height: 36px;
  background: #333;
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: background 0.2s;

  &:hover {
    background: #555;
  }
}

.order-detail-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 24px;
}

.order-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 60px 18px 32px;
  background: #fff;
  border-bottom: 1px solid #ececec;
  position: sticky;
  top: 0;
  z-index: 1;
}

.order-detail-id-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex-wrap: wrap;
}

.order-detail-id {
  font-size: 17px;
  font-weight: 600;
  color: #333;
  letter-spacing: 0.02em;
  
  word-break: break-all;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  
  white-space: nowrap;

  &.unpaid {
    background: #fff3e0;
    color: #e65100;
  }

  &.paid {
    background: #e8f5e9;
    color: #2e7d32;
  }

  &.shipping {
    background: #e3f2fd;
    color: #1565c0;
  }

  &.pending {
    background: #fff8e1;
    color: #f57c00;
  }

  &.completed {
    background: #f3e5f5;
    color: #6a1b9a;
  }

  &.cancelled {
    background: #fce4ec;
    color: #c62828;
  }
}

.order-detail-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.order-cancel-btn {
  flex-shrink: 0;
  padding: 10px 20px;
  background: #fff;
  color: #999;
  border: 1px solid #ddd;
  font-size: 13px;
  cursor: pointer;
  
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    color: #c62828;
    border-color: #c62828;
  }
}

.order-pay-methods {
  display: flex;
  gap: 10px;
}

.order-pay-btn {
  flex-shrink: 0;
  padding: 10px 20px;
  background: #c0a062;
  color: #fff;
  border: none;
  font-size: 13px;
  cursor: pointer;
  
  transition: background 0.2s;
  white-space: nowrap;

  &:hover {
    background: #a8884a;
  }

  &.order-pay-btn-chinapay {
    background: #e21e32;

    &:hover {
      background: #c41a2b;
    }
  }
}

.order-detail-section {
  margin: 16px 24px 0;
  background: #fff;
  padding: 20px 24px;
}

.order-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.order-section-count {
  font-size: 12px;
  color: #999;
  font-weight: 400;
}

.order-timeline {
  list-style: none;
  margin: 0;
  padding: 0;
}

.order-timeline-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  position: relative;

  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 24px;
    bottom: -4px;
    width: 2px;
    background: #ececec;
  }
}

.order-timeline-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ddd;
  flex-shrink: 0;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #ddd;
  z-index: 1;

  &.active {
    background: #c0a062;
    box-shadow: 0 0 0 1px #c0a062;
  }
}

.order-timeline-label {
  font-size: 13px;
  color: #888;
  
  min-width: 80px;
}

.order-timeline-value {
  font-size: 13px;
  color: #333;
  
  margin-left: auto;
}

.order-amount-list {
  display: flex;
  flex-direction: column;
}

.order-amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 0;

  & + & {
    border-top: 1px dashed #f0f0f0;
  }
}

.order-amount-row-total {
  margin-top: 4px;
  padding-top: 14px;
  border-top: 2px solid #333 !important;
}

.order-amount-label {
  font-size: 13px;
  color: #666;
  
}

.order-amount-value {
  font-size: 14px;
  color: #333;
  
  font-weight: 500;
}

.order-amount-discount {
  color: #c62828;
}

.order-amount-value-gold {
  color: #b8934d;
  font-weight: 600;
  font-size: 17px;
}

.order-amount-paid {
  color: #2e7d32;
}

.order-recipient-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.order-recipient-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.order-recipient-label {
  font-size: 13px;
  color: #999;
  
  min-width: 70px;
  flex-shrink: 0;
}

.order-recipient-value {
  font-size: 14px;
  color: #333;
  
  word-break: break-all;
  flex: 1;
}

.order-items-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.order-item-card {
  display: grid;
  grid-template-columns: 72px 1fr auto auto;
  gap: 14px;
  align-items: center;
  padding: 14px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
}

.order-item-image-wrap {
  width: 72px;
  height: 72px;
  background: #fff;
  border: 1px solid #ececec;
  overflow: hidden;
  flex-shrink: 0;
}

.order-item-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.order-item-image-placeholder {
  display: block;
  width: 100%;
  height: 100%;
  background: #f5f5f5;
}

.order-item-info {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.order-item-brand {
  font-size: 12px;
  color: #888;
  margin: 0;
  
  line-height: 1.3;
}

.order-item-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin: 0;
  
  line-height: 1.4;
  word-break: break-word;
}

.order-item-spec {
  font-size: 12px;
  color: #888;
  margin: 0;
  
  line-height: 1.3;
}

.order-item-price-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.order-item-unit-price {
  font-size: 13px;
  color: #b8934d;
  font-weight: 500;
  
}

.order-item-original-price {
  font-size: 12px;
  color: #bbb;
  text-decoration: line-through;
  
}

.order-item-qty-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 50px;
}

.order-item-qty-label {
  font-size: 11px;
  color: #aaa;
  
}

.order-item-qty {
  font-size: 14px;
  color: #555;
  
  font-weight: 500;
}

.order-item-line-total-col {
  text-align: right;
  min-width: 90px;
}

.order-item-line-total {
  font-size: 14px;
  color: #b8934d;
  font-weight: 600;
  
  white-space: nowrap;
}

.order-info-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.order-info-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.order-info-label {
  font-size: 13px;
  color: #999;
  
  min-width: 70px;
  flex-shrink: 0;
}

.order-info-value {
  font-size: 14px;
  color: #333;
  
  word-break: break-all;
  flex: 1;
}

.order-info-express {
  color: #1565c0;
  font-weight: 500;
  letter-spacing: 0.02em;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-wrap: wrap;
}

.express-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.express-no {
  color: #1565c0;
  font-weight: 500;
}

.express-track-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #c0a062;
  font-size: 12px;
  font-weight: 400;
  text-decoration: none;
  letter-spacing: 0;
  padding: 2px 8px;
  border: 1px solid #c0a062;
  border-radius: 3px;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #c0a062;
    color: #fff;
  }

  svg {
    flex-shrink: 0;
  }
}

.address-book-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.address-book-modal {
  position: relative;
  width: 100%;
  max-width: 640px;
  max-height: 80vh;
  background: #fff;
  padding: 28px 32px 32px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.28);
  overflow-y: auto;
}

.address-book-close,
.address-form-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 36px;
  height: 36px;
  background: #333;
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: #555;
  }
}

.address-book-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
  padding-right: 44px;
}

.address-book-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
  
}

.address-book-add-btn {
  padding: 9px 22px;
  background: #333;
  color: #fff;
  border: none;
  font-size: 13px;
  cursor: pointer;
  
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #555;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.address-book-limit {
  font-size: 13px;
  color: #999;
  margin: 0 0 20px;
  
}

.address-book-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;

  p {
    font-size: 15px;
    color: #999;
    margin: 0;
    
  }
}

.address-book-list {
  display: flex;
  flex-direction: column;
}

.address-book-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 0;
  border-top: 1px solid #ececec;

  &:first-child {
    border-top: none;
    padding-top: 4px;
  }
}

.address-book-item-main {
  flex: 1;
  min-width: 0;
}

.address-book-name {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 10px;
  
}

.address-book-phone {
  margin: 0 0 10px;
}

.address-book-phone-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #f0f0f0;
  font-size: 13px;
  color: #555;
  
}

.address-book-line {
  font-size: 14px;
  color: #555;
  margin: 0 0 4px;
  line-height: 1.5;
  

  &:last-child {
    margin-bottom: 0;
  }
}

.address-book-item-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  padding-top: 4px;
}

.address-book-edit-btn {
  padding: 8px 18px;
  background: #333;
  color: #fff;
  border: none;
  font-size: 13px;
  cursor: pointer;
  
  transition: background 0.2s;

  &:hover {
    background: #555;
  }
}

.address-book-delete-btn {
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  color: #999;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #333;
  }
}

.address-form-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.address-form-modal {
  position: relative;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  background: #fff;
  padding: 28px 32px 32px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.28);
  overflow-y: auto;
}

.address-form-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 24px;
  padding-right: 44px;
  
}

.address-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.address-form-textarea {
  resize: vertical;
  min-height: 96px;
  line-height: 1.5;
}

.address-form-save-btn {
  width: 100%;
  padding: 13px 20px;
  margin-top: 4px;
  background: #333;
  color: #fff;
  border: none;
  font-size: 14px;
  cursor: pointer;
  
  transition: background 0.2s;

  &:hover {
    background: #555;
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-active .account-modal-container,
.modal-fade-leave-active .account-modal-container {
  transition: transform 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .account-modal-container,
.modal-fade-leave-to .account-modal-container {
  transform: scale(0.96) translateY(-12px);
}

@media (max-width: 900px) {
  .account-modal-overlay {
    padding: 0;
    align-items: stretch;
  }

  .account-modal-container {
    max-width: none;
    max-height: none;
    height: 100%;
    border-radius: 0;
  }

  .overview-top,
  .overview-bottom {
    grid-template-columns: 1fr;
  }

  .points-card-content {
    padding: 24px 20px;
  }

  .points-num {
    font-size: 32px;
  }
}

@media (max-width: 600px) {
  .account-sidebar {
    width: 56px;
    padding: 16px 0 12px;
  }

  .sidebar-nav-item,
  .sidebar-logout {
    width: 40px;
    height: 40px;
  }

  .account-main-header,
  .account-overview,
  .account-profile,
  .account-list-page,
  .account-orders-page {
    padding-left: 16px;
    padding-right: 16px;
  }

  .orders-table-head,
  .orders-table-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .orders-col-actions {
    justify-content: flex-start;
  }

  .order-detail-modal {
    max-height: 95vh;
  }

  .order-detail-header {
    padding: 16px 52px 14px 18px;
    flex-direction: column;
    align-items: flex-start;
  }

  .order-detail-section {
    margin: 12px 14px 0;
    padding: 16px 18px;
  }

  .order-detail-actions {
    width: 100%;
    flex-direction: column;
    gap: 8px;
  }

  .order-cancel-btn,
  .order-pay-btn {
    width: 100%;
    padding: 12px 20px;
    text-align: center;
  }

  .order-item-card {
    grid-template-columns: 60px 1fr;
    grid-template-areas:
      "image info"
      "qty total";
    gap: 10px;
    padding: 12px;
  }

  .order-item-image-wrap {
    grid-area: image;
    width: 60px;
    height: 60px;
  }

  .order-item-info {
    grid-area: info;
  }

  .order-item-qty-col {
    grid-area: qty;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .order-item-line-total-col {
    grid-area: total;
    align-self: center;
  }

  .order-timeline-label {
    min-width: 70px;
  }

  .order-recipient-label,
  .order-info-label {
    min-width: 60px;
  }

  .profile-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .profile-actions {
    width: 100%;
  }

  .profile-btn {
    flex: 1;
  }

  .profile-field-row,
  .profile-field-row-name,
  .profile-field-row-inline {
    flex-direction: column;
    align-items: stretch;
  }

  .profile-field-region,
  .profile-field-salutation {
    flex: 1;
  }

  .profile-label-hidden {
    display: none;
  }
}

/* 零售小票样式 */
.receipts-section,
.coupons-section {
  flex: 1;
  overflow-y: auto;
  padding: 0 28px 28px;

  .list-empty {
    padding: 60px 20px;
    text-align: center;

    svg {
      color: #ccc;
      margin-bottom: 16px;
    }

    .empty-text {
      font-size: 16px;
      color: #666;
      margin: 0 0 8px 0;
    }

    .empty-hint {
      font-size: 13px;
      color: #999;
      margin: 0;
    }
  }
}

.list-section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 0 16px;

  .list-section-icon {
    color: #b9aa6b;
    display: flex;
    align-items: center;
  }

  .list-section-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
}

.list-divider {
  height: 1px;
  background: #e5e5e5;
  margin-bottom: 8px;
}

.account-list-page {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.list-toolbar {
  padding: 20px 28px 16px;
  flex-shrink: 0;
  border-bottom: 1px solid #e5e5e5;
}

.profile-meta {
  font-size: 13px;
  color: #999;
}

.account-orders-page {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.orders-section {
  flex: 1;
  overflow-y: auto;
  padding: 0 28px 28px;
}

.receipts-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 20px 0;
}

.receipt-card {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.receipt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px dashed #e5e5e5;

  .receipt-store {
    font-size: 14px;
    font-weight: 600;
    color: #333;
  }

  .receipt-date {
    font-size: 12px;
    color: #999;
  }
}

.receipt-body {
  .receipt-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    .receipt-number {
      font-size: 12px;
      color: #666;
    }

    .receipt-amount {
      font-size: 16px;
      font-weight: 600;
      color: #333;
    }
  }

  .receipt-points {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: #b9aa6b;
    font-weight: 500;

    svg {
      flex-shrink: 0;
    }
  }
}

/* 優惠券样式 */
.coupons-tabs {
  display: flex;
  gap: 8px;
  padding: 16px 0;
  border-bottom: 1px solid #e5e5e5;
}

.coupons-tab {
  padding: 8px 20px;
  border: none;
  background: #f5f5f5;
  color: #666;
  font-size: 14px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #e8e8e8;
  }

  &.active {
    background: #b9aa6b;
    color: #fff;
  }
}

.coupons-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 0;
}

.coupon-card {
  display: flex;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s;
  background: #fff;

  &:hover:not(.disabled) {
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  &.disabled {
    opacity: 0.6;
  }

  &.expired {
    background: #f9f9f9;
  }
}

.coupon-left {
  width: 140px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #b9aa6b 0%, #9f834d 100%);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: -10px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #fff;
    box-shadow: inset 0 0 0 10px #fff;
  }

  .coupon-value-large {
    display: flex;
    align-items: baseline;
    margin-bottom: 8px;

    .coupon-currency-small {
      font-size: 16px;
      margin-right: 4px;
    }

    .coupon-amount-large {
      font-size: 40px;
      font-weight: 700;
      line-height: 1;
    }
  }

  .coupon-condition {
    font-size: 12px;
    opacity: 0.9;
  }

  .coupon-free-shipping-icon {
    margin-bottom: 8px;
  }

  .coupon-free-shipping-text {
    font-size: 18px;
    font-weight: 600;
  }
}

.coupon-free-shipping .coupon-left {
  background: linear-gradient(135deg, #4a90d9 0%, #357abd 100%);
}

.coupon-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.coupon-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.coupon-type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;

  &.coupon-type-universal {
    background: #e8f5e9;
    color: #2e7d32;
  }

  &.coupon-type-product {
    background: #e3f2fd;
    color: #1565c0;
  }

  &.coupon-type-brand {
    background: #f3e5f5;
    color: #7b1fa2;
  }

  &.coupon-type-shipping {
    background: #e0f2f1;
    color: #00695c;
  }
}

.coupon-applicable {
  font-size: 12px;
  color: #666;
  margin: 0 0 8px 0;
  display: flex;
  align-items: flex-start;
  gap: 4px;

  .applicable-label {
    color: #999;
    flex-shrink: 0;
  }

  .applicable-value {
    color: #555;
  }
}

.coupon-right {
  flex: 1;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.coupon-details {
  flex: 1;

  .coupon-description {
    font-size: 13px;
    color: #666;
    margin: 0 0 8px 0;
  }

  .coupon-validity {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #999;

    svg {
      flex-shrink: 0;
    }
  }
}

.coupon-status {
  flex-shrink: 0;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;

  &.status-available {
    background: #e8f5e9;
    color: #4caf50;
  }

  &.status-used {
    background: #f5f5f5;
    color: #999;
  }

  &.status-expired {
    background: #ffebee;
    color: #f44336;
  }
}

@media (max-width: 768px) {
  .receipts-grid {
    grid-template-columns: 1fr;
  }

  .coupon-card {
    flex-direction: column;
  }

  .coupon-left {
    width: 100%;
    padding: 16px;

    &::after {
      display: none;
    }
  }

  .coupon-right {
    flex-direction: column;
    align-items: flex-start;
  }
}

/* U盾轉賬提示彈窗 */
.ushield-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100;
}

.ushield-modal-container {
  background: #fff;
  width: 420px;
  max-width: 90vw;
  padding: 40px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.ushield-modal-icon {
  color: #c0a062;
  margin-bottom: 20px;
}

.ushield-modal-text {
  font-size: 16px;
  color: #333;
  line-height: 1.6;
  margin: 0 0 28px;
}

.ushield-modal-btn {
  padding: 12px 48px;
  font-size: 15px;
  background: #2a2a2a;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.85;
  }
}

/* 支付寶支付彈窗 */
.alipay-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100;
}

.alipay-modal-container {
  background: #fff;
  width: 480px;
  max-width: 90vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.chinapay-modal {
  width: 900px;
  height: 85vh;
}

.alipay-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
}

.alipay-modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.alipay-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
}

.alipay-modal-body {
  flex: 1;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.alipay-iframe {
  width: 100%;
  height: 100%;
  min-height: 60vh;
  border: 0;
}

.chinapay-modal .alipay-iframe {
  width: 100%;
  height: 100%;
  min-height: 60vh;
}

.chinapay-waiting {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.chinapay-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #eee;
  border-top-color: #333;
  border-radius: 50%;
  animation: chinapay-spin 0.8s linear infinite;
}

@keyframes chinapay-spin {
  to { transform: rotate(360deg); }
}

.chinapay-waiting-text {
  font-size: 14px;
  color: #666;
  margin: 0;
  text-align: center;
}

.alipay-modal-footer {
  padding: 14px 24px 18px;
  border-top: 1px solid #eee;
  text-align: center;
}

.alipay-paid-btn {
  min-width: 160px;
  padding: 10px 28px;
  border: none;
  background: #1a1a1a;
  color: #d4af37;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.alipay-paid-hint {
  margin: 8px 0 0;
  color: #999;
  font-size: 12px;
  line-height: 1.5;
}

/* 積分兌換訂單 */
.score-orders-table .orders-table-head,
.score-orders-table .orders-table-row {
  grid-template-columns: 2fr 1.3fr 1fr 130px;
}

.score-orders-table .score-orders-col-status {
  text-align: right;
}

.score-order-product {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.score-order-img {
  width: 56px;
  height: 56px;
  object-fit: contain;
  border: 1px solid #f0f0f0;
  background: #fafafa;
  flex-shrink: 0;
}

.score-order-img-placeholder {
  display: inline-block;
}

.score-order-title {
  min-width: 0;
}

.score-order-title .order-number {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.score-order-status-col {
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.score-order-express {
  font-size: 12px;
  color: #999;
  margin: 0;
  text-align: right;
  word-break: break-all;
}

.order-status.status-1 {
  color: #e67e22;
  background: #fdf6ec;
}

.order-status.status-2 {
  color: #2980b9;
  background: #eef5fb;
}

.order-status.status-3 {
  color: #27ae60;
  background: #eefdf3;
}

@media (max-width: 768px) {
  .score-orders-table .orders-table-head {
    display: none;
  }

  .score-orders-table .orders-table-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .score-order-status-col {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}
</style>
